import { useMemo, useRef, useState, useEffect, memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSubscriptionGuard } from "@/components/SubscriptionGuard";
import { batchStore } from "@/stores/batchStore";
import { Upload, FileSpreadsheet, X, Wand2, Send, Loader2, CheckCircle2, XCircle, AlertCircle, Sparkles, Download, Timer, ImageIcon, Trash2, Save, FolderTree } from "lucide-react";

const CONCURRENCY = 6;
const FLUSH_MS = 300; // throttle setRows to at most ~3x/s

// Cost estimates per LLM call (Emergent LLM Key rough averages, 2026-02)
// generate-fields: ~800 tokens output, gpt-4o-mini/gemini-flash ≈ $0.001
// vision-filter: ~3 image tokens + ~200 output, gemini-2.5-flash ≈ $0.003
const COST_GENERATE_FIELDS = 0.001;
const COST_VISION_FILTER = 0.003;

// --- Synthetic dataset generator for load testing ---
const SYN_TEMPLATES = [
  { cat: "medicamento", brands: ["Dipirona", "Paracetamol", "Ibuprofeno", "Amoxicilina", "Omeprazol", "Losartana", "Sinvastatina", "Ranitidina", "Cetoconazol", "Loratadina"], units: ["500mg", "750mg", "200mg", "40mg", "50mg", "20mg", "10mg"], qty: [""] },
  { cat: "cosmético", brands: ["Shampoo Pantene", "Condicionador Seda", "Creme Nivea", "Sabonete Dove", "Hidratante Vasenol", "Perfume Natura", "Batom Avon", "Loção Johnson", "Desodorante Rexona"], units: ["100ml", "200ml", "400ml", "500ml"], qty: ["Hidratação", "Reparação", "Brilho", "Volume"] },
  { cat: "alimento", brands: ["Nescau Achocolatado", "Leite Ninho", "Café Pilão", "Arroz Tio João", "Feijão Camil", "Óleo Soya", "Açúcar União", "Farinha Dona Benta", "Biscoito Bauducco"], units: ["400g", "500g", "1kg", "2kg", "5kg"], qty: [""] },
  { cat: "eletrônico", brands: ["Notebook Dell Inspiron", "Smartphone Samsung Galaxy", "TV LG UHD", "Mouse Logitech", "Teclado Redragon", "Fone JBL", "Câmera GoPro Hero", "SSD Kingston", "Monitor AOC"], units: ["8GB", "128GB", "256GB", "50pol", "24pol", "1TB"], qty: [""] },
  { cat: "pet", brands: ["Ração Golden", "Ração Premier", "Areia Pipicat", "Coleira Petz", "Petisco Dreamies", "Shampoo Sanol", "Antipulgas Frontline"], units: ["3kg", "10kg", "15kg", "1L", "500g"], qty: [""] },
  { cat: "casa", brands: ["Detergente Ypê", "Sabão OMO", "Amaciante Comfort", "Água Sanitária Qboa", "Desinfetante Veja", "Papel Higiênico Neve", "Pano Bettanin"], units: ["500ml", "1L", "3L", "2kg"], qty: [""] },
];

function generateSynthetic(count) {
  const items = [];
  const seen = new Set();
  let guard = 0;
  while (items.length < count && guard < count * 5) {
    const t = SYN_TEMPLATES[Math.floor(Math.random() * SYN_TEMPLATES.length)];
    const brand = t.brands[Math.floor(Math.random() * t.brands.length)];
    const unit = t.units[Math.floor(Math.random() * t.units.length)];
    const qty = t.qty[Math.floor(Math.random() * t.qty.length)];
    const parts = [brand, qty, unit].filter(Boolean);
    const name = `${parts.join(" ")} #${items.length + 1}`;
    const key = name.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      items.push({ name, category: t.cat });
    }
    guard++;
  }
  return items;
}

function fmtDuration(ms) {
  if (!ms || ms < 0) return "0s";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m ${rs}s`;
}

async function pMap(items, mapper, concurrency, onProgress, abortRef) {
  const results = new Array(items.length);
  let index = 0;
  let done = 0;
  const workers = new Array(concurrency).fill(0).map(async () => {
    while (true) {
      if (abortRef && abortRef.current) break;
      const i = index++;
      if (i >= items.length) break;
      try {
        results[i] = await mapper(items[i], i);
      } catch (e) {
        results[i] = { error: e?.message || String(e) };
        // Special sentinel: abort the whole batch on LLM quota failure
        if (e?.message === "llm_quota_abort" && abortRef) {
          abortRef.current = true;
        }
      }
      done++;
      if (onProgress) onProgress(done, items.length);
    }
  });
  await Promise.all(workers);
  return results;
}

// --- Status chip: defined OUTSIDE to avoid remounting on every render ---
const STATUS_MAP = {
  queued: { bg: "bg-zinc-100 text-zinc-600", label: "FILA", spin: false, icon: null },
  processing: { bg: "bg-blue-50 text-blue-700", label: "PROCESSANDO", spin: true, icon: "loader" },
  ready: { bg: "bg-emerald-50 text-emerald-700", label: "PRONTO", spin: false, icon: "check" },
  no_images: { bg: "bg-amber-50 text-amber-700", label: "SEM IMAGEM", spin: false, icon: "alert" },
  error: { bg: "bg-red-50 text-red-700", label: "ERRO", spin: false, icon: "x" },
  publishing: { bg: "bg-indigo-50 text-indigo-700", label: "PUBLICANDO", spin: true, icon: "loader" },
  published: { bg: "bg-emerald-100 text-emerald-800", label: "PUBLICADO", spin: false, icon: "check" },
  publish_error: { bg: "bg-red-50 text-red-700", label: "FALHOU", spin: false, icon: "x" },
  duplicate: { bg: "bg-amber-100 text-amber-800", label: "DUPLICADO", spin: false, icon: "alert" },
  filtering: { bg: "bg-violet-50 text-violet-700", label: "FILTRANDO", spin: true, icon: "loader" },
};

const StatusIcon = memo(function StatusIcon({ kind }) {
  if (kind === "loader") return <Loader2 className="w-3 h-3 animate-spin" />;
  if (kind === "check") return <CheckCircle2 className="w-3 h-3" />;
  if (kind === "alert") return <AlertCircle className="w-3 h-3" />;
  if (kind === "x") return <XCircle className="w-3 h-3" />;
  return null;
});

const StatusChip = memo(function StatusChip({ status, title, onClick }) {
  const s = STATUS_MAP[status] || STATUS_MAP.queued;
  const isError = status === "publish_error" || status === "error" || status === "duplicate";
  const isClickable = isError && !!onClick && !!title;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-mono tracking-widest ${s.bg} ${isClickable ? "cursor-pointer underline decoration-dotted underline-offset-2 hover:brightness-95" : ""}`}
      title={title || ""}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      data-testid={isClickable ? "batch-row-error-chip" : undefined}
    >
      <StatusIcon kind={s.icon} />
      {s.label}
    </span>
  );
});

// --- Thumbnail with error handling via React state (avoids DOM mutation bugs) ---
const Thumb = memo(function Thumb({ src }) {
  const [broken, setBroken] = useState(false);
  if (broken || !src) {
    return <div className="w-6 h-6 rounded border border-zinc-200 bg-zinc-100" />;
  }
  return (
    <div className="w-6 h-6 rounded overflow-hidden border border-zinc-200 bg-zinc-50">
      <img
        src={src}
        alt=""
        className="w-full h-full object-cover"
        loading="lazy"
        onError={() => setBroken(true)}
      />
    </div>
  );
});

// --- Row: memoized. Only re-renders when its own row data changes ---
const BatchRow = memo(function BatchRow({ r, onToggle, onEditPrice, onEditField, onShowError, onPasteManualImage, onPasteManualCategory, onPasteManualAnvisa, onAddRxTerm, onRetryImages, publishTarget }) {
  const imgsNotOk =
    r.status === "no_images" || ["mixed", "uncertain", "rejected"].includes(r.vision_status || "");
  const canRetryImages =
    imgsNotOk && !["processing", "filtering", "publishing", "published"].includes(r.status);
  return (
    <tr className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`batch-row-${r.id}`}>
      <td className="p-2 text-center">
        <input
          type="checkbox"
          checked={!!r.selected}
          onChange={() => onToggle(r.id)}
          disabled={r.status === "processing" || r.status === "error"}
          className="rounded"
          data-testid={`batch-checkbox-${r.id}`}
        />
      </td>
      <td className="p-2 align-top">
        <div className="font-medium text-zinc-900 text-xs truncate max-w-[180px]" title={r.name}>{r.name}</div>
        <div className="text-[10px] text-zinc-500 font-mono uppercase">{r.category || "—"}</div>
        {r.ml_blocked && publishTarget === "mercadolivre" && (
          <div
            className="mt-1 inline-block text-[9px] font-semibold text-red-800 bg-red-50 border border-red-300 px-1.5 py-0.5 rounded-full"
            title={r.ml_block_reason || "Medicamento humano — não publicar no ML"}
            data-testid={`ml-blocked-badge-${r.id}`}
          >
            💊 Rx bloqueado no ML
          </div>
        )}
      </td>
      <td className="p-2 align-top max-w-[400px]">
        <div className="text-xs text-zinc-700 line-clamp-2" title={r.description}>
          {r.description
            ? r.description.replace(/<[^>]+>/g, " ").slice(0, 150) + "..."
            : <span className="text-zinc-400">—</span>}
        </div>
      </td>
      <td className="p-2 align-top">
        <div className="flex gap-1 items-center flex-wrap">
          {(r.images || []).slice(0, 3).map((u, i) => (
            <Thumb key={`${r.id}-${i}-${u}`} src={u} />
          ))}
          <span className="text-[10px] text-zinc-500 font-mono ml-1">{r.images?.length || 0}</span>
          {r.vision_filtered != null && r.vision_filtered > 0 && (
            <span className="text-[9px] font-mono text-violet-700 bg-violet-50 px-1 rounded" title={`${r.vision_filtered} imagem(ns) descartada(s) pelo Vision`}>
              -{r.vision_filtered}
            </span>
          )}
          {r.vision_status === "confident" && (
            <span
              className="text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5 rounded-full"
              title="Vision confirmou marca + produto corretos"
              data-testid={`vision-badge-confident-${r.id}`}
            >
              ✓ OK
            </span>
          )}
          {r.vision_status === "mixed" && (
            <span
              className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded-full"
              title="Algumas imagens têm dúvida — revise antes de publicar"
              data-testid={`vision-badge-mixed-${r.id}`}
            >
              ⚠ Revise
            </span>
          )}
          {r.vision_status === "uncertain" && (
            <span
              className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded-full"
              title="Vision teve dúvida — revise cada imagem"
              data-testid={`vision-badge-uncertain-${r.id}`}
            >
              ⚠ Revise
            </span>
          )}
          {r.vision_status === "rejected" && (
            <span
              className="text-[9px] font-semibold text-red-700 bg-red-50 border border-red-300 px-1.5 py-0.5 rounded-full"
              title="Vision rejeitou tudo — cole uma URL manual"
              data-testid={`vision-badge-rejected-${r.id}`}
            >
              ✗ Manual
            </span>
          )}
          {r.vision_status === "manual" && (
            <span
              className="text-[9px] font-semibold text-blue-700 bg-blue-50 border border-blue-300 px-1.5 py-0.5 rounded-full"
              title="Imagens definidas manualmente por você"
              data-testid={`vision-badge-manual-${r.id}`}
            >
              ✎ Manual
            </span>
          )}
          <button
            type="button"
            onClick={() => onPasteManualImage(r.id)}
            className="text-[9px] font-semibold text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 border border-zinc-300 px-1.5 py-0.5 rounded-full"
            title="Colar URL manual da imagem"
            data-testid={`paste-manual-img-${r.id}`}
          >
            + URL
          </button>
          {canRetryImages && (
            <button
              type="button"
              onClick={() => onRetryImages(r.id)}
              className="text-[9px] font-semibold text-orange-700 bg-orange-50 border border-orange-300 px-1.5 py-0.5 rounded-full hover:bg-orange-100"
              title="Buscar novamente com IA: reformula a busca e tenta outras fontes de imagem, depois revalida com o Vision"
              data-testid={`retry-images-${r.id}`}
            >
              ↻ Nova busca
            </button>
          )}
          {publishTarget === "mercadolivre" && (
            <button
              type="button"
              onClick={() => onPasteManualCategory(r.id)}
              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${
                r.ml_category_id
                  ? "text-emerald-800 bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
                  : "text-blue-700 bg-blue-50 border-blue-300 hover:bg-blue-100"
              }`}
              title={r.ml_category_id ? `Categoria ML definida: ${r.ml_category_id}` : "Colar categoria ML manual (MLBxxxx)"}
              data-testid={`paste-manual-ml-cat-${r.id}`}
            >
              {r.ml_category_id ? `✓ ${r.ml_category_id}` : "+ MLB"}
            </button>
          )}
          {publishTarget === "mercadolivre" && (
            <button
              type="button"
              onClick={() => onPasteManualAnvisa(r.id)}
              className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full border ${
                r.anvisa
                  ? "text-emerald-800 bg-emerald-50 border-emerald-300 hover:bg-emerald-100"
                  : "text-purple-700 bg-purple-50 border-purple-300 hover:bg-purple-100"
              }`}
              title={r.anvisa ? `ANVISA: ${r.anvisa}` : "Colar registro ANVISA (obrigatório em medicamentos)"}
              data-testid={`paste-manual-anvisa-${r.id}`}
            >
              {r.anvisa ? `✓ ANV` : "+ ANV"}
            </button>
          )}
          {publishTarget === "mercadolivre" && !r.ml_blocked && (
            <button
              type="button"
              onClick={() => onAddRxTerm(r.id)}
              className="text-[9px] font-semibold text-red-700 bg-red-50 border border-red-300 px-1.5 py-0.5 rounded-full hover:bg-red-100"
              title="Marcar como medicamento: adiciona uma palavra ao filtro Rx e desmarca este produto (e similares) da publicação no Mercado Livre"
              data-testid={`add-rx-term-${r.id}`}
            >
              + Rx
            </button>
          )}
        </div>
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.price}
          onChange={(e) => onEditPrice(r.id, e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs"
          placeholder="cheio"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.promotional_price || ""}
          onChange={(e) => onEditField(r.id, "promotional_price", e.target.value)}
          className="h-8 rounded-md border-amber-200 bg-amber-50/40 font-mono text-xs"
          placeholder="promo"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.stock || ""}
          onChange={(e) => onEditField(r.id, "stock", e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs w-16"
          placeholder="0"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.weight || ""}
          onChange={(e) => onEditField(r.id, "weight", e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs w-16"
          placeholder="kg"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.height || ""}
          onChange={(e) => onEditField(r.id, "height", e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs w-16"
          placeholder="cm"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.width || ""}
          onChange={(e) => onEditField(r.id, "width", e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs w-16"
          placeholder="cm"
        />
      </td>
      <td className="p-2 align-top">
        <Input
          value={r.depth || ""}
          onChange={(e) => onEditField(r.id, "depth", e.target.value)}
          className="h-8 rounded-md border-zinc-300 font-mono text-xs w-16"
          placeholder="cm"
        />
      </td>
      <td className="p-2 align-top">
        <StatusChip
          status={r.status}
          title={r.publish_message || r.error_msg || r.vision_note || ""}
          onClick={() => onShowError && onShowError(r)}
        />
        {r.nuvemshop_id && <div className="text-[10px] font-mono text-zinc-500 mt-1">ID {r.nuvemshop_id}</div>}
      </td>
    </tr>
  );
}, (prev, next) => {
  const a = prev.r, b = next.r;
  return (
    prev.publishTarget === next.publishTarget &&
    a.id === b.id &&
    a.status === b.status &&
    a.selected === b.selected &&
    a.price === b.price &&
    a.promotional_price === b.promotional_price &&
    a.stock === b.stock &&
    a.weight === b.weight &&
    a.height === b.height &&
    a.width === b.width &&
    a.depth === b.depth &&
    a.description === b.description &&
    a.images === b.images &&
    a.vision_filtered === b.vision_filtered &&
    a.vision_status === b.vision_status &&
    a.ml_category_id === b.ml_category_id &&
    a.anvisa === b.anvisa &&
    a.ml_blocked === b.ml_blocked &&
    a.publish_message === b.publish_message &&
    a.nuvemshop_id === b.nuvemshop_id
  );
});

export default function BatchPublish() {
  // Hydrate from module-level store so navigating away/back preserves in-flight work
  const _initial = batchStore.getState();
  const [inputText, setInputText] = useState(_initial.inputText || "");
  const [fileItems, setFileItems] = useState([]);
  const [fileName, setFileName] = useState(_initial.fileName || "");
  const [headersDetected, setHeadersDetected] = useState(_initial.headersDetected || false);
  const [rows, setRowsState] = useState(_initial.rows || []);
  const [prepProgress, setPrepProgressState] = useState(_initial.prepProgress || { done: 0, total: 0 });
  const [pubProgress, setPubProgressState] = useState(_initial.pubProgress || { done: 0, total: 0 });
  const [preparing, setPreparingState] = useState(_initial.preparing || false);
  const [publishing, setPublishingState] = useState(_initial.publishing || false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [recategorizing, setRecategorizing] = useState(false);
  const [recatProgress, setRecatProgress] = useState({ done: 0, total: 0 });
  const [retryingFailed, setRetryingFailed] = useState(false);
  const [retryProgress, setRetryProgress] = useState({ done: 0, total: 0 });
  const [parseDiag, setParseDiag] = useState(null);
  const [rowFilter, setRowFilter] = useState("");
  const [batchId, setBatchId] = useState(_initial.batchId || null);
  const [batchName, setBatchName] = useState(_initial.batchName || "");
  const [savingBatch, setSavingBatch] = useState(false);
  const [prepTiming, setPrepTimingState] = useState(_initial.prepTiming || null);
  const [pubTiming, setPubTimingState] = useState(_initial.pubTiming || null);
  const [llmCost, setLlmCostState] = useState(_initial.llmCost || 0);
  const llmCostRef = useRef(_initial.llmCost || 0);
  const [useVisionFilter, setUseVisionFilter] = useState(true);
  const [tick, setTick] = useState(0); // forces re-render every 1s during ops
  const fileRef = useRef(null);
  const { canPublish, subscription, banner: subBanner, reload: reloadSub, isSuperadmin } = useSubscriptionGuard();

  // ---------- Multi-ERP target selector ----------
  // Fetch which providers the user has connected and which support batch create.
  // Providers with `create` and matching connected credentials show in the dropdown.
  const [connectedErps, setConnectedErps] = useState([]);  // [{key, name}]
  const [publishTarget, setPublishTarget] = useState(""); // "nuvemshop" | "bling"
  const [errorModalRow, setErrorModalRow] = useState(null);
  const [publishReport, setPublishReport] = useState(null);
  const [forceDuplicates, setForceDuplicates] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: provs }, { data: creds }] = await Promise.all([
          api.get("/erp/providers"),
          api.get("/erp/credentials"),
        ]);
        if (cancelled) return;
        const credMap = new Set((creds || []).map((c) => c.provider));
        const eligible = (provs || [])
          .filter((p) => p.supports_create && credMap.has(p.key))
          .map((p) => ({ key: p.key, name: p.name }));
        setConnectedErps(eligible);
        // Persist selection: localStorage → nuvemshop (preferred) → first eligible
        const savedKey = localStorage.getItem("batchPublish.target") || "";
        const isSavedValid = eligible.some((e) => e.key === savedKey);
        const preferred =
          (isSavedValid && savedKey) ||
          eligible.find((e) => e.key === "nuvemshop")?.key ||
          eligible[0]?.key ||
          "";
        setPublishTarget(preferred);
      } catch {
        setConnectedErps([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist target ERP whenever it changes
  useEffect(() => {
    if (publishTarget) {
      try { localStorage.setItem("batchPublish.target", publishTarget); } catch (_) {}
    }
  }, [publishTarget]);

  // iter 47 — When user picks Mercado Livre as target, auto-detect and
  // deselect human medications (Rx/OTC that would require ANVISA/ATC per
  // SKU — unpublishable in bulk). Uses backend heuristic on name+category.
  // Vitamins, supplements, foods, hygiene, cosmetics stay selected.
  // The `ml_blocked` flag on each row drives a visual badge in the row UI.
  // When user picks any other target, we CLEAR the ml_blocked flags so the
  // rows can be re-selected normally.
  const [mlBlockedCount, setMlBlockedCount] = useState(0);
  const [mlClassifyNonce, setMlClassifyNonce] = useState(0);
  useEffect(() => {
    // Reset ML flags when switching away
    if (publishTarget !== "mercadolivre") {
      const changed = rowsRef.current.some((r) => r.ml_blocked);
      if (changed) {
        rowsRef.current = rowsRef.current.map((r) => {
          if (!r.ml_blocked) return r;
          const { ml_blocked, ml_block_reason, ...rest } = r;
          return rest;
        });
        setRows(rowsRef.current.slice());
      }
      setMlBlockedCount(0);
      return;
    }
    // Only run if we have rows to classify
    const rowsToClassify = rowsRef.current
      .filter((r) => r.name && r.status !== "error")
      .map((r) => ({ id: r.id, name: r.name, category: r.category || "" }));
    if (!rowsToClassify.length) {
      setMlBlockedCount(0);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/erp/mercadolivre/classify-batch", {
          items: rowsToClassify,
        });
        if (cancelled) return;
        const blockedIds = new Set((data?.blocked || []).map((b) => b.id));
        const blockedReasonMap = Object.fromEntries(
          (data?.blocked || []).map((b) => [b.id, b.reason_pt || b.reason]),
        );
        rowsRef.current = rowsRef.current.map((r) => {
          if (blockedIds.has(r.id)) {
            return {
              ...r,
              ml_blocked: true,
              ml_block_reason: blockedReasonMap[r.id] || "Medicamento humano",
              selected: false, // auto-deselect
            };
          }
          if (r.ml_blocked) {
            // was blocked before, no longer → clear flag
            const { ml_blocked, ml_block_reason, ...rest } = r;
            return rest;
          }
          return r;
        });
        setRows(rowsRef.current.slice());
        setMlBlockedCount(blockedIds.size);
        if (blockedIds.size > 0) {
          toast.info(
            `${blockedIds.size} medicamento(s) humano(s) desmarcado(s) — Mercado Livre exige ANVISA/ATC por SKU`,
            { duration: 8000, icon: "💊" }
          );
        }
      } catch (_e) {
        // silent — endpoint failure shouldn't block the batch
        setMlBlockedCount(0);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishTarget, rows.length, mlClassifyNonce]);

  // iter 51 — "+Rx" button: add a custom word to the ML medication filter and
  // re-run the batch classification so matching rows get auto-deselected.
  const addRxTerm = useCallback(async (id) => {
    const row = rowsRef.current.find((r) => r.id === id);
    if (!row) return;
    const firstWord = ((row.name || "").split(/\s+/).find((w) => /^[a-zA-ZÀ-ú]{3,}/.test(w)) || "").toLowerCase();
    const term = window.prompt(
      "Bloquear como medicamento no Mercado Livre.\n\nTodo produto cujo NOME contiver esta palavra será desmarcado automaticamente (vale para lotes futuros também):",
      firstWord
    );
    if (term === null) return;
    const clean = term.trim().toLowerCase();
    if (clean.length < 3) {
      toast.error("Termo muito curto (mínimo 3 letras)");
      return;
    }
    try {
      await api.post("/erp/mercadolivre/rx-terms", { term: clean });
      toast.success(`"${clean}" adicionado ao filtro Rx — reclassificando o lote`, { icon: "💊" });
      setMlClassifyNonce((n) => n + 1);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar termo Rx");
    }
  }, []);

  // Tier limit for gating "load test" buttons + batch cap.
  // iter 56 ladder: Start 100 · Pro 500 · Business 2500 · Enterprise 5000 · Agências 10000.
  const TIER_LIMITS = { micro: 100, start: 100, pro: 500, business: 2500, enterprise: 5000, agencias: 10000 };
  // Plan monthly price (BRL) — used to display operational cost per item.
  // Superadmin uses Agências price for the "quanto essa operação me custou" display.
  const TIER_PRICES_BRL = { micro: 8.90, start: 89.00, pro: 169.00, business: 349.00, enterprise: 699.00, agencias: 1299.00 };
  const activeTier = (subscription?.tier || "").toLowerCase();
  const isActivePaid = subscription?.status === "active";
  const batchMaxSize = isSuperadmin
    ? 10000
    : (isActivePaid && TIER_LIMITS[activeTier]) || 1000;
  // Plan price (BRL/month) tied to the current plan tier for the operational
  // cost formula shown to the user: (plan_price / plan_limit) * items_processed.
  // Fall back to Start pricing when user isn't paid (trial baseline).
  const planPriceBRL = isSuperadmin
    ? TIER_PRICES_BRL.agencias
    : (isActivePaid && TIER_PRICES_BRL[activeTier]) || TIER_PRICES_BRL.start;
  const planLimitForOp = isSuperadmin
    ? TIER_LIMITS.agencias
    : (isActivePaid && TIER_LIMITS[activeTier]) || TIER_LIMITS.start;
  const opCostPerItemBRL = planLimitForOp > 0 ? planPriceBRL / planLimitForOp : 0;
  const testAllowedLimit = isSuperadmin
    ? 10000
    : isActivePaid
    ? TIER_LIMITS[activeTier] || 10
    : 10;

  // Setters that mirror to the global store so background loops keep the UI in
  // sync when the component re-mounts after route change.
  const setRows = useCallback((v) => {
    setRowsState(v);
    const next = typeof v === "function" ? v(rowsRef.current) : v;
    batchStore.setState({ rows: next });
  }, []);
  const setPrepProgress = useCallback((v) => { setPrepProgressState(v); batchStore.setState({ prepProgress: v }); }, []);
  const setPubProgress = useCallback((v) => { setPubProgressState(v); batchStore.setState({ pubProgress: v }); }, []);
  const setPreparing = useCallback((v) => { setPreparingState(v); batchStore.setState({ preparing: v }); }, []);
  const setPublishing = useCallback((v) => { setPublishingState(v); batchStore.setState({ publishing: v }); }, []);
  const setPrepTiming = useCallback((v) => {
    setPrepTimingState(v);
    batchStore.setState((s) => ({ prepTiming: typeof v === "function" ? v(s.prepTiming) : v }));
  }, []);
  const setPubTiming = useCallback((v) => {
    setPubTimingState(v);
    batchStore.setState((s) => ({ pubTiming: typeof v === "function" ? v(s.pubTiming) : v }));
  }, []);
  const setLlmCost = useCallback((v) => { setLlmCostState(v); batchStore.setState({ llmCost: v }); }, []);

  // Subscribe to store so any external write (from background async loops) also
  // pushes into local component state.
  useEffect(() => {
    return batchStore.subscribe((s) => {
      setRowsState(s.rows);
      setPrepProgressState(s.prepProgress);
      setPubProgressState(s.pubProgress);
      setPreparingState(s.preparing);
      setPublishingState(s.publishing);
      setPrepTimingState(s.prepTiming);
      setPubTimingState(s.pubTiming);
      setLlmCostState(s.llmCost);
      if (s.rows && rowsRef.current !== s.rows) rowsRef.current = s.rows;
    });
  }, []);

  // Persist inputText / fileName to store when they change (so re-mount restores form UX too)
  useEffect(() => { batchStore.setState({ inputText, fileName, headersDetected }); }, [inputText, fileName, headersDetected]);

  // Live ticker while operations run
  useEffect(() => {
    if (!preparing && !publishing) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setLlmCost(llmCostRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [preparing, publishing]);

  // --- Ref-based rows store with throttled flush (avoids 1000+ re-renders) ---
  // Initialized from batchStore so we preserve in-flight work across route changes.
  const rowsRef = useRef(_initial.rows || []);
  const flushTimerRef = useRef(null);
  const dirtyRef = useRef(false);
  const llmQuotaFailedRef = useRef(false);
  const pausePrepRef = useRef(false);

  const flushRows = useCallback(() => {
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      if (dirtyRef.current) {
        dirtyRef.current = false;
        const snap = rowsRef.current.slice();
        setRowsState(snap);
        batchStore.setState({ rows: snap });
      }
    }, FLUSH_MS);
  }, []);

  const flushRowsImmediate = useCallback(() => {
    if (flushTimerRef.current) {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = null;
    }
    dirtyRef.current = false;
    const snap = rowsRef.current.slice();
    setRowsState(snap);
    batchStore.setState({ rows: snap });
  }, []);

  const updateRowAt = useCallback((idx, patch) => {
    const cur = rowsRef.current[idx];
    if (!cur) return;
    rowsRef.current[idx] = { ...cur, ...patch };
    dirtyRef.current = true;
    flushRows();
  }, [flushRows]);

  const updateRowById = useCallback((id, patch) => {
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    rowsRef.current[idx] = { ...rowsRef.current[idx], ...patch };
    dirtyRef.current = true;
    flushRows();
  }, [flushRows]);

  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearTimeout(flushTimerRef.current);
    };
  }, []);

  const parsedItems = useMemo(() => {
    if (fileItems.length) return fileItems;
    const out = [];
    const seen = new Set();
    for (const line of inputText.split(/\r?\n/)) {
      const t = line.trim();
      if (!t) continue;
      const parts = t.split(/\t|,|;/).map((p) => p.trim());
      const name = parts[0] || "";
      const category = parts.slice(1).join(" ").trim();
      if (!name) continue;
      const key = `${name.toLowerCase()}|${category.toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ name, category });
    }
    return out;
  }, [inputText, fileItems]);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFileItems(data.items);
      setHeadersDetected(!!data.headers_detected);
      setParseDiag({
        detected: data.detected_columns || [],
        unmatched: data.unmatched_headers || [],
        raw: data.raw_header_row || [],
      });
      // Count rows that actually have a CSV price so user knows if extraction worked
      const rowsWithPrice = (data.items || []).filter(
        (it) => it.price != null || it.promotional_price != null
      ).length;
      const extraNote = data.headers_detected ? " (campos extras detectados — IA será pulada onde possível)" : "";
      toast.success(`${data.count} itens carregados${extraNote}`);

      if (data.headers_detected) {
        const cols = new Set(data.detected_columns || []);
        const missing = [];
        if (!cols.has("price")) missing.push("preco");
        if (!cols.has("promotional_price")) missing.push("preco_promocional");
        if (missing.length && rowsWithPrice === 0) {
          toast.warning(
            `Coluna(s) NÃO detectada(s): ${missing.join(", ")}. ` +
            `Cabeçalhos vistos: ${(data.raw_header_row || []).join(" | ")}. ` +
            `Renomeie as colunas para: preco, preco_promocional.`,
            { duration: 15000 }
          );
        }
      }
    } catch (e) {
      toast.error("Erro ao ler arquivo");
      setFileName("");
      setFileItems([]);
      setHeadersDetected(false);
      setParseDiag(null);
    }
  };

  const clearFile = () => {
    setFileItems([]);
    setFileName("");
    setHeadersDetected(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/parse-file/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "modelo_lote_produtos.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Falha ao baixar modelo");
    }
  };

  // iter 55 — "Atualizar só preços": items from the sheet with an identifier
  // (SKU or GTIN) + a price can have ONLY their price updated on the ERP.
  const toNum = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(String(v).replace(",", "."));
    return isNaN(n) ? null : n;
  };
  const priceUpdatableItems = useMemo(
    () =>
      parsedItems.filter(
        (it) =>
          (it.sku || it.gtin) &&
          (toNum(it.price) !== null || toNum(it.promotional_price) !== null || toNum(it.stock) !== null)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsedItems]
  );
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [priceProgress, setPriceProgress] = useState({ done: 0, total: 0 });

  const updatePricesOnly = async () => {
    const targetKey = publishTarget || "nuvemshop";
    const targetName = connectedErps.find((e) => e.key === targetKey)?.name || targetKey;
    const items = priceUpdatableItems.map((it, i) => ({
      id: String(i),
      sku: String(it.sku || ""),
      gtin: String(it.gtin || ""),
      name: it.name || "",
      price: toNum(it.price) !== null ? toNum(it.price) : toNum(it.promotional_price),
      promotional_price: toNum(it.promotional_price),
      stock: toNum(it.stock) !== null ? Math.round(toNum(it.stock)) : null,
    }));
    if (!items.length) {
      toast.error("A planilha precisa ter SKU ou código de barras + preço/estoque");
      return;
    }
    const hasStock = items.some((it) => it.stock !== null);
    if (!window.confirm(
      `Atualizar SOMENTE ${hasStock ? "preços e estoque" : "os preços"} de ${items.length} item(ns) no ${targetName}?\n\nOs produtos são localizados pelo SKU ou código de barras — nada além disso será alterado.`
    )) return;
    setUpdatingPrices(true);
    setPriceProgress({ done: 0, total: items.length });
    let updated = 0, notFound = 0, failed = 0;
    const failures = [];
    try {
      for (let i = 0; i < items.length; i += 20) {
        const chunk = items.slice(i, i + 20);
        const { data } = await api.post(`/erp/${targetKey}/update-prices`, { items: chunk });
        updated += data.updated || 0;
        notFound += data.not_found || 0;
        failed += data.failed || 0;
        (data.details || []).forEach((d) => {
          if (!d.ok) failures.push({ id: d.id, name: d.name, ok: false, message: d.message });
        });
        setPriceProgress({ done: Math.min(i + chunk.length, items.length), total: items.length });
      }
      if (failures.length === 0) {
        toast.success(`${updated} preço(s) atualizado(s) no ${targetName}`);
      } else {
        toast.warning(`${updated} atualizado(s) · ${notFound} não encontrado(s) · ${failed} falha(s)`, { duration: 8000 });
        setPublishReport({
          kind: "prices",
          target: `Atualização de preços · ${targetName}`,
          total: items.length,
          success: updated,
          failures,
          duplicates: [],
        });
      }
    } catch (e) {
      toast.error("Falha na atualização de preços: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally {
      setUpdatingPrices(false);
    }
  };

  // iter 57 — Import the existing Nuvemshop catalog and enhance ONLY the
  // ML-specific gaps (indicação de uso, medidas) with AI. Prices, images and
  // descriptions come as-is from the store — ML category is resolved by the
  // existing publish pipeline. Then the user just selects Mercado Livre.
  const [importingNS, setImportingNS] = useState(false);
  const [nsImportProgress, setNsImportProgress] = useState({ phase: "", done: 0, total: 0 });

  const importFromNuvemshop = async () => {
    if (rowsRef.current.length && !window.confirm("Isso substitui a tabela atual pelos produtos existentes da sua Nuvemshop. Continuar?")) return;
    setImportingNS(true);
    setNsImportProgress({ phase: "Listando produtos...", done: 0, total: 0 });
    try {
      const imported = [];
      for (let p = 1; p <= 20; p++) {
        const { data } = await api.get(`/erp/nuvemshop/import-products?page=${p}&per_page=50`);
        if (data.error) {
          toast.error("Nuvemshop: " + data.error);
          break;
        }
        imported.push(...(data.items || []));
        setNsImportProgress({ phase: `Listando produtos... (${imported.length})`, done: imported.length, total: 0 });
        if (!data.has_more) break;
      }
      if (!imported.length) {
        toast.warning("Nenhum produto publicado encontrado na Nuvemshop");
        return;
      }
      const built = imported.map((it, i) => ({
        id: `row-ns-${i}-${Date.now()}`,
        name: it.name,
        category: it.category || "",
        status: (it.images || []).length ? "ready" : "no_images",
        selected: (it.images || []).length > 0,
        images: it.images || [],
        price: it.price || "",
        promotional_price: it.promotional_price || "",
        sku: it.sku || "",
        gtin: it.gtin || "",
        handle: "",
        description: it.description || "",
        usage: "",
        seo_title: "",
        seo_description: "",
        stock: it.stock || "",
        weight: it.weight || "",
        height: it.height || "",
        width: it.width || "",
        depth: it.depth || "",
        brand: "",
        nuvemshop_id: it.nuvemshop_id,
        vision_filtered: null,
        vision_note: "Importado da Nuvemshop",
        prefilled: true,
      }));
      rowsRef.current = built;
      setRows(built.slice());
      toast.success(`${built.length} produto(s) importado(s) da Nuvemshop`);

      // AI pass: fill ONLY missing indicação (usage) + measures
      const missing = built
        .map((r, i) => i)
        .filter((i) => {
          const r = built[i];
          return !r.usage || !r.weight || !r.height || !r.width || !r.depth;
        });
      if (missing.length) {
        setNsImportProgress({ phase: "IA completando indicação e medidas...", done: 0, total: missing.length });
        llmQuotaFailedRef.current = false;
        let done = 0;
        await pMap(
          missing,
          async (idx) => {
            const item = rowsRef.current[idx];
            try {
              const { data: f } = await api.post("/generate-fields", { name: item.name, category: item.category });
              llmCostRef.current += COST_GENERATE_FIELDS;
              updateRowAt(idx, {
                usage: item.usage || f.usage || "",
                weight: item.weight || (f.weight != null ? String(f.weight) : ""),
                height: item.height || (f.height != null ? String(f.height) : ""),
                width: item.width || (f.width != null ? String(f.width) : ""),
                depth: item.depth || (f.depth != null ? String(f.depth) : ""),
              });
            } catch (_e) { /* keep row as-is */ }
            done += 1;
            setNsImportProgress({ phase: "IA completando indicação e medidas...", done, total: missing.length });
          },
          CONCURRENCY,
          null,
          llmQuotaFailedRef
        );
        flushRowsImmediate();
        setLlmCost(llmCostRef.current);
      }
      toast.success("Pronto! Selecione Mercado Livre em PUBLICAR EM e publique os anúncios", { duration: 10000, icon: "🛒" });
    } catch (e) {
      toast.error("Falha na importação: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally {
      setImportingNS(false);
    }
  };

  // -- Per-row preparation pipeline (search + LLM + vision). Reads the row
  // from rowsRef by index so it can be re-run by "Continuar preparação". --
  const prepareRowAtIndex = async (idx) => {
    const item = rowsRef.current[idx];
    if (!item) return;
    updateRowAt(idx, { status: "processing" });
    try {
      // Only search for images if not provided in spreadsheet
      const needsImages = !(item.images || []).length;
      // CSV price is ABSOLUTE source of truth. If any price present, we won't
      // let the LLM's suggested_price ever overwrite it.
      const hasCsvPrice = !!(item.price || item.promotional_price);
      // Only ask LLM if any required non-price field is missing.
      const needsLLM = !item.description || !item.seo_title || !item.seo_description || !item.sku;

      const promises = [];
      if (needsImages) {
        promises.push(api.post("/search", { items: [{ name: item.name, category: item.category, brand: item.brand || "" }] }));
      } else {
        promises.push(Promise.resolve({ data: [{ images: item.images }] }));
      }
      if (needsLLM) {
        promises.push(
          api.post("/generate-fields", { name: item.name, category: item.category })
            .then((r) => { llmCostRef.current += COST_GENERATE_FIELDS; return r; })
        );
      } else {
        promises.push(Promise.resolve({ data: {} }));
      }
      const [searchRes, genRes] = await Promise.all(promises);
      const rawImgs = searchRes.data?.[0]?.images || [];
      const f = genRes.data || {};

      let finalImgs = rawImgs;
      let uncertainImgs = [];
      let filteredCount = null;
      let visionNote = "";
      let visionStatus = "none"; // none | confident | uncertain | rejected

      // Only apply Vision filter if images came from web search (not pre-filled)
      if (useVisionFilter && needsImages && rawImgs.length > 0) {
        updateRowAt(idx, { status: "filtering", images: rawImgs });
        try {
          const vRes = await api.post("/vision/filter-images", {
            name: item.name,
            category: item.category,
            brand: item.brand || "",
            images: rawImgs,
          });
          llmCostRef.current += COST_VISION_FILTER;
          const kept = vRes.data?.kept || [];
          const uncertain = vRes.data?.uncertain || [];
          // Merge: confident first, then uncertain (for review)
          finalImgs = [...kept, ...uncertain];
          uncertainImgs = uncertain;
          filteredCount = Math.max(0, rawImgs.length - finalImgs.length);
          visionNote = vRes.data?.note || "";
          visionStatus = kept.length > 0
            ? (uncertain.length > 0 ? "mixed" : "confident")
            : (uncertain.length > 0 ? "uncertain" : "rejected");
        } catch (ve) {
          visionNote = "Vision indisponível";
        }
      }

      updateRowAt(idx, {
        status: finalImgs.length ? "ready" : "no_images",
        images: finalImgs,
        uncertain_images: uncertainImgs,
        vision_status: visionStatus,
        // CSV IS THE SOURCE OF TRUTH. Price fields NEVER get filled by AI
        // if any CSV price value was provided (regular or promo).
        price: item.price || (hasCsvPrice ? "" : String(f.suggested_price || "")),
        promotional_price: item.promotional_price || "",
        sku: item.sku || f.sku || "",
        handle: item.handle || f.handle || "",
        description: item.description || f.description || "",
        usage: item.usage || f.usage || "",
        seo_title: item.seo_title || f.seo_title || "",
        seo_description: item.seo_description || f.seo_description || "",
        stock: item.stock || (f.stock != null ? String(f.stock) : ""),
        weight: item.weight || (f.weight != null ? String(f.weight) : ""),
        height: item.height || (f.height != null ? String(f.height) : ""),
        width: item.width || (f.width != null ? String(f.width) : ""),
        depth: item.depth || (f.depth != null ? String(f.depth) : ""),
        selected: finalImgs.length > 0,
        vision_filtered: filteredCount,
        vision_note: visionNote,
      });
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      updateRowAt(idx, { status: "error", error_msg: detail });
      // 402 = LLM quota exhausted → abort remaining items to save time
      if (status === 402) {
        llmQuotaFailedRef.current = true;
        throw new Error("llm_quota_abort");
      }
    }
  };

  // -- Shared prep loop: runs the pipeline over a list of row indexes.
  // Supports pause (⏸ button sets pausePrepRef → remaining rows stay "queued").
  // iter 54 — silent batch save (no prompt). Used by auto-save on pause.
  const persistBatch = async (nameToUse) => {
    const payload = {
      id: batchId || undefined,
      name: nameToUse,
      rows: rowsRef.current,
      file_name: fileName,
      headers_detected: !!headersDetected,
      prep_progress: prepProgress,
      pub_progress: pubProgress,
      llm_cost: llmCostRef.current || 0,
    };
    const { data } = await api.post("/batches", payload);
    setBatchId(data.id);
    setBatchName(data.name);
    batchStore.setState({ batchId: data.id, batchName: data.name });
    return data;
  };

  const autoSaveBatch = async () => {
    const defaultName =
      batchName ||
      (fileName ? fileName.replace(/\.[^.]+$/, "") : "") ||
      `Lote de ${new Date().toLocaleString("pt-BR")}`;
    return persistBatch(defaultName.trim());
  };

  const runPrepLoop = async (indexes) => {
    setPreparing(true);
    pausePrepRef.current = false;
    setPrepTiming({ startMs: Date.now(), endMs: null });
    setPrepProgress({ done: 0, total: indexes.length });
    llmQuotaFailedRef.current = false;

    await pMap(
      indexes,
      async (rowIdx) => {
        if (pausePrepRef.current) return; // skip — stays "queued" for resume
        await prepareRowAtIndex(rowIdx);
      },
      CONCURRENCY,
      (done, total) => setPrepProgress({ done, total }),
      llmQuotaFailedRef
    );
    flushRowsImmediate();
    setPreparing(false);
    setPrepTiming((t) => (t ? { ...t, endMs: Date.now() } : t));
    setLlmCost(llmCostRef.current);
    const pendingLeft = rowsRef.current.filter((r) =>
      r.status === "queued" || r.status === "processing" || r.status === "filtering"
    ).length;
    if (llmQuotaFailedRef.current) {
      llmQuotaFailedRef.current = false;
      toast.error("Saldo do Emergent LLM Key esgotado. Recarregue em Profile → Universal Key e tente novamente.", { duration: 10000 });
    } else if (pausePrepRef.current && pendingLeft > 0) {
      // iter 54 — auto-save the batch on pause so nothing is lost
      try {
        const saved = await autoSaveBatch();
        toast.info(
          `Preparação pausada — ${pendingLeft.toLocaleString("pt-BR")} item(ns) na fila. Lote "${saved.name}" salvo automaticamente. Retome com "Continuar preparação" (aqui ou em Meus Lotes).`,
          { duration: 12000, icon: "⏸️" }
        );
      } catch (_e) {
        toast.warning(
          `Preparação pausada — ${pendingLeft.toLocaleString("pt-BR")} item(ns) na fila. Não consegui salvar automaticamente: clique em "Salvar lote".`,
          { duration: 12000, icon: "⏸️" }
        );
      }
    } else {
      toast.success("Preparação concluída — revise os itens antes de publicar");
    }
  };

  const prepareAll = async () => {
    if (parsedItems.length === 0) {
      toast.error("Adicione produtos primeiro");
      return;
    }
    if (parsedItems.length > batchMaxSize) {
      toast.error(`Máximo ${batchMaxSize.toLocaleString("pt-BR")} itens por lote no seu plano`);
      return;
    }
    const initial = parsedItems.map((it, i) => ({
      id: `row-${i}-${Date.now()}`,
      name: it.name,
      category: it.category || "",
      status: "queued",
      // iter 52 — start DESELECTED: a row only becomes selected when prep
      // finishes with images. Fixes the publish-button count including
      // queued/error rows (user saw 787 prontos vs 8362 no botão).
      selected: false,
      images: Array.isArray(it.images) ? it.images : [],
      price: typeof it.price === "number" ? String(it.price) : (it.price || ""),
      promotional_price: typeof it.promotional_price === "number" ? String(it.promotional_price) : (it.promotional_price || ""),
      sku: it.sku || "",
      handle: it.handle || "",
      description: it.description || "",
      usage: it.usage || "",
      seo_title: it.seo_title || "",
      seo_description: it.seo_description || "",
      stock: it.stock != null ? String(it.stock) : "",
      weight: it.weight != null ? String(it.weight) : "",
      height: it.height != null ? String(it.height) : "",
      width: it.width != null ? String(it.width) : "",
      depth: it.depth != null ? String(it.depth) : "",
      gtin: it.gtin || "",
      brand: it.brand || "",
      vision_filtered: null,
      vision_note: "",
      prefilled: !!(it.price || it.sku || it.description || it.seo_title),
    }));
    rowsRef.current = initial;
    setRows(initial.slice());
    llmCostRef.current = 0;
    setLlmCost(0);
    await runPrepLoop(initial.map((_, i) => i));
  };

  // iter 52 — Resume: re-run prep ONLY for rows still pending (queued or
  // interrupted mid-processing). Works in-session after ⏸ Pausar and also
  // after reopening a saved batch (statuses are persisted in /api/batches).
  const resumePreparation = async () => {
    const pendingIdx = [];
    rowsRef.current.forEach((r, i) => {
      if (r.status === "queued" || r.status === "processing" || r.status === "filtering") pendingIdx.push(i);
    });
    if (!pendingIdx.length) {
      toast.info("Nenhum item pendente de preparação");
      return;
    }
    toast.info(`Retomando preparação de ${pendingIdx.length.toLocaleString("pt-BR")} item(ns)...`, { icon: "▶️" });
    await runPrepLoop(pendingIdx);
  };

  const publishSelected = async () => {
    const selected = rowsRef.current.filter((r) => r.selected && r.status !== "publishing" && r.status !== "published");
    if (!selected.length) {
      toast.error("Selecione ao menos 1 item pronto para publicar");
      return;
    }
    // Pre-check: subscription must be active
    if (!canPublish) {
      toast.error("Publicação bloqueada: seu plano não está ativo ou o trial já atingiu o limite. Ver Planos.", { duration: 8000 });
      return;
    }
    // Pre-check: ERP target must be connected
    if (!publishTarget) {
      toast.error("Nenhum ERP conectado que suporte publicação em lote. Conecte Nuvemshop ou Bling em Configurações.", { duration: 8000 });
      return;
    }
    const targetName = connectedErps.find((e) => e.key === publishTarget)?.name || publishTarget;
    // Warn about missing critical fields
    const incomplete = selected.filter((r) => !r.name || !parseFloat(r.price) || !r.images?.length);
    if (incomplete.length) {
      const proceed = window.confirm(
        `${incomplete.length} de ${selected.length} item(s) estão sem nome, preço ou imagem — o ${targetName} pode rejeitá-los.\n\nDeseja publicar mesmo assim?`
      );
      if (!proceed) return;
    }
    setPublishing(true);
    setPubTiming({ startMs: Date.now(), endMs: null });
    setPubProgress({ done: 0, total: selected.length });
    let successCount = 0;
    let subscriptionBlocked = false;
    let subscriptionBlockMsg = "";
    const runResults = [];
    await pMap(
      selected,
      async (item) => {
        if (subscriptionBlocked) {
          // Abort remaining items silently — mark as blocked
          updateRowById(item.id, {
            status: "publish_error",
            publish_message: "Bloqueado (assinatura)",
          });
          runResults.push({ id: item.id, name: item.name, ok: false, message: "Bloqueado (assinatura)" });
          return;
        }
        updateRowById(item.id, { status: "publishing" });
        try {
          const promoNum = parseFloat(item.promotional_price);
          const priceNum = parseFloat(item.price) || 0;
          // Only send promo when it is strictly less than regular price.
          // If user filled only promo (or promo >= price), we treat it as no promo,
          // since the price field already holds the correct value (auto-promoted
          // upstream when only promo was provided).
          const promoValid = promoNum > 0 && promoNum < priceNum ? promoNum : null;
          const stockNum = parseInt(item.stock, 10);
          const parseDim = (v) => {
            const n = parseFloat(v);
            return isNaN(n) || n <= 0 ? null : n;
          };
          const { data } = await api.post(`/erp/publish/${publishTarget || "nuvemshop"}`, {
            name: item.name,
            category: item.category,
            description: item.description || `<p>${item.name}</p>`,
            usage: item.usage,
            seo_title: item.seo_title || item.name.slice(0, 60),
            seo_description: item.seo_description || item.name.slice(0, 160),
            handle: item.handle,
            price: priceNum,
            promotional_price: promoValid,
            sku: item.sku,
            stock: !isNaN(stockNum) && stockNum > 0 ? stockNum : 0,
            weight: parseDim(item.weight),
            height: parseDim(item.height),
            width: parseDim(item.width),
            depth: parseDim(item.depth),
            images: item.images,
            published: true,
            force: !!forceDuplicates,
            gtin: item.gtin || "",
            brand: item.brand || "",
            // iter 44 — user-supplied ML category override (used when the
            // auto-resolver returns NONE and the user pastes a category_id
            // manually via the "+ MLB" button per row).
            ml_category_id: item.ml_category_id || "",
            anvisa: item.anvisa || "",
          });
          if (data.ok) {
            successCount++;
            updateRowById(item.id, {
              status: "published",
              publish_message: data.message,
              nuvemshop_id: data.product_id,
            });
            runResults.push({ id: item.id, name: item.name, ok: true });
          } else if (data.duplicate) {
            // Bling/Nuvemshop rejected as duplicate — flag row for user review
            updateRowById(item.id, {
              status: "duplicate",
              publish_message: data.message,
              nuvemshop_id: data.existing_product_id,
            });
            runResults.push({ id: item.id, name: item.name, ok: false, duplicate: true, message: data.message || "Duplicata" });
          } else {
            updateRowById(item.id, {
              status: "publish_error",
              publish_message: data.message,
              nuvemshop_id: data.product_id,
            });
            runResults.push({ id: item.id, name: item.name, ok: false, message: data.message || "Erro desconhecido" });
          }
        } catch (e) {
          const status = e.response?.status;
          const detail = e.response?.data?.detail;
          // 402 = subscription/paywall — abort remaining items
          if (status === 402) {
            subscriptionBlocked = true;
            subscriptionBlockMsg = typeof detail === "object" ? (detail.message || detail.cta || "") : (detail || "");
            updateRowById(item.id, {
              status: "publish_error",
              publish_message: `Bloqueado: ${subscriptionBlockMsg}`,
            });
            runResults.push({ id: item.id, name: item.name, ok: false, message: `Bloqueado: ${subscriptionBlockMsg}` });
          } else {
            const msg = formatApiErrorDetail(detail);
            updateRowById(item.id, {
              status: "publish_error",
              publish_message: msg,
            });
            runResults.push({ id: item.id, name: item.name, ok: false, message: msg || "Erro de rede" });
          }
        }
      },
      3,
      (done, total) => setPubProgress({ done, total })
    );
    flushRowsImmediate();
    setPublishing(false);
    setPubTiming((t) => (t ? { ...t, endMs: Date.now() } : t));
    reloadSub();
    if (subscriptionBlocked) {
      toast.error(
        `Publicação abortada — assinatura necessária. ${subscriptionBlockMsg} ${successCount > 0 ? `(${successCount} produtos foram publicados antes do bloqueio)` : "(nenhum produto foi publicado)"}`,
        { duration: 12000 }
      );
    } else if (successCount === 0) {
      toast.error(`Nenhum produto publicado. Verifique os erros na tabela abaixo.`, { duration: 8000 });
    } else if (successCount < selected.length) {
      toast.warning(`Publicação parcial · ${successCount}/${selected.length} publicados. Verifique os erros restantes.`, { duration: 8000 });
    } else {
      toast.success(`Publicação concluída · ${successCount}/${selected.length} produtos publicados no ${targetName}`);
      toast.info(
        `Aguarde de 10 a 20 minutos para o ${targetName} sincronizar o catálogo. O tempo é do ERP, não do Magic SIP.`,
        { duration: 12000, icon: "⏱️" }
      );
    }
    // iter 51 — post-publish report: open a summary modal whenever at least
    // one item failed (includes duplicates), grouping failures by reason.
    const failures = runResults.filter((r) => !r.ok && !r.duplicate);
    const duplicates = runResults.filter((r) => r.duplicate);
    if (failures.length || duplicates.length) {
      setPublishReport({
        target: targetName,
        total: selected.length,
        success: successCount,
        failures,
        duplicates,
      });
    }
  };

  // Re-select only the failed rows from the last report and republish them.
  const resendReportFailures = () => {
    if (!publishReport?.failures?.length) return;
    const failedIds = new Set(publishReport.failures.map((f) => f.id));
    rowsRef.current = rowsRef.current.map((r) => ({ ...r, selected: failedIds.has(r.id) }));
    setRows(rowsRef.current.slice());
    setPublishReport(null);
    setTimeout(() => publishSelected(), 150);
  };

  const toggleRow = useCallback((id) => {
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    const row = rowsRef.current[idx];
    // Guard: user cannot manually re-select ML-blocked rows while ML is target.
    // They must switch target OR click the row's "override" mechanism (future).
    if (row.ml_blocked && !row.selected) {
      toast.warning(
        "Medicamento humano — não pode ser publicado no Mercado Livre em lote (falta ANVISA/ATC). Troque o ERP alvo ou publique manualmente no ML.",
        { duration: 6000 }
      );
      return;
    }
    rowsRef.current[idx] = { ...row, selected: !row.selected };
    setRows(rowsRef.current.slice());
  }, []);

  const toggleAll = useCallback((val) => {
    rowsRef.current = rowsRef.current.map((r) => ({
      ...r,
      // "Selecionar prontos" = only rows with status PRONTO (ready).
      // ML-blocked rows stay deselected.
      selected: val && r.status === "ready" && !r.ml_blocked,
    }));
    setRows(rowsRef.current.slice());
  }, []);

  // iter 53 — header master checkbox: check = select ALL selectable rows
  // (any status except error/in-flight and not ML-blocked); uncheck = clear all.
  const toggleSelectAllRows = useCallback((val) => {
    rowsRef.current = rowsRef.current.map((r) => ({
      ...r,
      selected: val && r.status !== "error" && r.status !== "processing" && r.status !== "filtering" && !r.ml_blocked,
    }));
    setRows(rowsRef.current.slice());
    toast.success(val ? "Todos os itens selecionados" : "Seleção removida de todos os itens");
  }, []);

  const editField = useCallback((id, field, value) => {
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    rowsRef.current[idx] = { ...rowsRef.current[idx], [field]: value };
    setRows(rowsRef.current.slice());
  }, []);

  const editPrice = useCallback((id, value) => editField(id, "price", value), [editField]);

  const pasteManualImage = useCallback((id) => {
    const row = rowsRef.current.find((r) => r.id === id);
    const currentUrls = (row?.images || []).join("\n");
    const raw = window.prompt(
      "Cole 1 ou mais URLs de imagem (uma por linha) para este produto:",
      currentUrls
    );
    if (raw == null) return; // user cancelled
    const urls = raw
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter((s) => /^https?:\/\//i.test(s))
      .slice(0, 5);
    if (urls.length === 0) {
      toast.error("Nenhuma URL válida (precisa começar com http:// ou https://)");
      return;
    }
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    rowsRef.current[idx] = {
      ...rowsRef.current[idx],
      images: urls,
      uncertain_images: [],
      vision_status: "manual",
      status: "ready",
      publish_message: "",
    };
    setRows(rowsRef.current.slice());
    toast.success(`${urls.length} imagem(ns) colada(s) manualmente`);
  }, []);

  const pasteManualCategory = useCallback(async (id) => {
    const row = rowsRef.current.find((r) => r.id === id);
    if (!row) return;
    // Fetch top predictions to help the user pick
    let hint = "";
    try {
      const params = new URLSearchParams({ title: row.name || "", category: row.category || "" });
      const { data } = await api.get(`/erp/mercadolivre/predict-category?${params}`);
      const preds = data?.predictions || [];
      if (preds.length > 0) {
        hint = "\n\nSugestões do ML para este produto:\n" +
          preds.slice(0, 5).map((p) => `  ${p.category_id} — ${p.category_name}${p.domain_name ? ` (${p.domain_name})` : ""}`).join("\n");
      }
    } catch {}
    const current = row.ml_category_id || "";
    const raw = window.prompt(
      `Cole o category_id do Mercado Livre para este produto (formato MLB seguido de números).\n` +
      `Encontre em: https://api.mercadolibre.com/sites/MLB/categories${hint}`,
      current
    );
    if (raw == null) return;
    const v = raw.trim().toUpperCase();
    if (v && !/^MLB\d{3,10}$/.test(v)) {
      toast.error("Formato inválido — precisa ser MLB seguido de 3-10 dígitos");
      return;
    }
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    rowsRef.current[idx] = { ...rowsRef.current[idx], ml_category_id: v };
    setRows(rowsRef.current.slice());
    toast.success(v ? `Categoria ML definida: ${v}` : "Categoria ML removida");
  }, []);

  const pasteManualAnvisa = useCallback((id) => {
    const row = rowsRef.current.find((r) => r.id === id);
    if (!row) return;
    const current = row.anvisa || "";
    const raw = window.prompt(
      "Cole o número de registro ANVISA deste medicamento (formato 1.XXXX.XXXX.XXX-X).\n" +
      "Consulte em: consultas.anvisa.gov.br → busca por nome do medicamento",
      current
    );
    if (raw == null) return;
    const v = raw.trim();
    // Accept any non-empty digits+dots+dashes (ML validates full format server-side)
    if (v && !/^[0-9.\-]+$/.test(v)) {
      toast.error("Formato inválido — só números, pontos e traços");
      return;
    }
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    if (idx < 0) return;
    rowsRef.current[idx] = { ...rowsRef.current[idx], anvisa: v };
    setRows(rowsRef.current.slice());
    toast.success(v ? `ANVISA definido: ${v}` : "ANVISA removido");
  }, []);

  const loadSynthetic = (n) => {
    const items = generateSynthetic(n);
    const lines = items.map((it) => `${it.name}, ${it.category}`).join("\n");
    setInputText(lines);
    clearFile();
    toast.success(`${items.length} produtos sintéticos gerados`);
  };

  const bulkDeletePublished = async () => {
    const publishedRows = rowsRef.current.filter((r) => r.status === "published" && r.nuvemshop_id);
    if (!publishedRows.length) {
      toast.error("Nenhum produto publicado neste lote");
      return;
    }
    const targetKey = publishTarget || "nuvemshop";
    const targetName = connectedErps.find((e) => e.key === targetKey)?.name || targetKey;
    const confirmText = window.prompt(
      `Isso vai APAGAR PERMANENTEMENTE ${publishedRows.length} produto(s) do seu ${targetName}.\n\nDigite APAGAR para confirmar:`
    );
    if (confirmText !== "APAGAR") {
      toast.info("Cancelado");
      return;
    }
    setBulkDeleting(true);
    try {
      const ids = publishedRows.map((r) => String(r.nuvemshop_id));
      const { data } = await api.post(`/erp/bulk-delete/${targetKey}`, { product_ids: ids });
      // Only mark rows that were actually deleted (based on details[].ok). Others
      // stay as "published" so the user can retry.
      const okSet = new Set(
        (data.details || []).filter((d) => d.ok).map((d) => String(d.product_id))
      );
      const firstFailureMsg =
        (data.details || []).find((d) => !d.ok && d.message)?.message || "";
      publishedRows.forEach((r) => {
        if (okSet.has(String(r.nuvemshop_id))) {
          updateRowById(r.id, {
            status: "publish_error",
            publish_message: `Apagado do ${targetName}`,
            nuvemshop_id: null,
          });
        } else {
          // Keep as published but update message so user knows delete failed
          updateRowById(r.id, {
            publish_message:
              firstFailureMsg ||
              `Falha ao apagar do ${targetName} (verifique escopo/token)`,
          });
        }
      });
      if (data.deleted > 0 && data.failed === 0) {
        toast.success(`${data.deleted} produto(s) apagado(s) do ${targetName}`);
        toast.info(
          `Aguarde de 10 a 20 minutos para o ${targetName} remover completamente do catálogo público. O tempo é do ERP.`,
          { duration: 12000, icon: "⏱️" }
        );
      } else if (data.deleted > 0 && data.failed > 0) {
        toast.warning(`${data.deleted} apagado(s) · ${data.failed} falha(s). Clique em FALHOU para ver o erro.`, { duration: 8000 });
        toast.info(
          `Aguarde de 10 a 20 minutos para o ${targetName} sincronizar as remoções bem-sucedidas.`,
          { duration: 10000, icon: "⏱️" }
        );
      } else {
        toast.error(
          `Nenhum produto apagado (${data.failed} falha(s)). ${firstFailureMsg}`,
          { duration: 12000 }
        );
      }
    } catch (e) {
      toast.error("Falha ao apagar em massa: " + (e.response?.data?.detail || e.message));
    } finally {
      setBulkDeleting(false);
    }
  };

  // iter 50 — Move already-published Nuvemshop products from "Magic SIP"
  // to the real spreadsheet category (creates the category if needed).
  const recategorizePublished = async () => {
    const rowsToFix = rowsRef.current.filter((r) => r.nuvemshop_id && (r.category || "").trim());
    if (!rowsToFix.length) {
      toast.error("Nenhum produto publicado com categoria na planilha");
      return;
    }
    setRecategorizing(true);
    setRecatProgress({ done: 0, total: rowsToFix.length });
    let updated = 0, failed = 0;
    try {
      for (let i = 0; i < rowsToFix.length; i += 25) {
        const chunk = rowsToFix.slice(i, i + 25);
        const { data } = await api.post("/erp/nuvemshop/recategorize", {
          items: chunk.map((r) => ({ product_id: String(r.nuvemshop_id), category: r.category || "" })),
        });
        const byId = new Map((data.details || []).map((d) => [String(d.product_id), d]));
        chunk.forEach((r) => {
          const d = byId.get(String(r.nuvemshop_id));
          if (d?.ok) updateRowById(r.id, { publish_message: d.message || `Categoria atualizada: ${r.category}` });
          else if (d) updateRowById(r.id, { publish_message: `Falha categoria: ${d.message || ""}`.slice(0, 200) });
        });
        updated += data.updated || 0;
        failed += data.failed || 0;
        setRecatProgress({ done: Math.min(i + chunk.length, rowsToFix.length), total: rowsToFix.length });
      }
      if (failed === 0) toast.success(`${updated} produto(s) movido(s) para a categoria da planilha`);
      else toast.warning(`${updated} atualizado(s) · ${failed} falha(s). Veja a mensagem na linha.`, { duration: 8000 });
    } catch (e) {
      toast.error("Falha ao reorganizar categorias: " + (formatApiErrorDetail(e.response?.data?.detail) || e.message));
    } finally {
      setRecategorizing(false);
    }
  };

  // Save the current batch to /api/batches so the user can reopen it later.
  const saveBatch = async () => {
    if (!rowsRef.current.length) {
      toast.error("Nada para salvar — nenhum item preparado");
      return;
    }
    setSavingBatch(true);
    try {
      const suggestedName = batchName || (fileName ? fileName.replace(/\.[^.]+$/, "") : "");
      const nameInput = window.prompt("Nome do lote:", suggestedName || `Lote de ${new Date().toLocaleString("pt-BR")}`);
      if (nameInput === null) {
        setSavingBatch(false);
        return;
      }
      const data = await persistBatch(nameInput.trim());
      toast.success(`Lote "${data.name}" salvo`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao salvar lote");
    } finally {
      setSavingBatch(false);
    }
  };

  // iter 53 — Per-row retry: re-search images via AI fallback (reformulated
  // query + other sources) and re-run Vision so the row can turn ✓ OK.
  const retryRowImages = useCallback(async (id) => {
    const row = rowsRef.current.find((r) => r.id === id);
    if (!row || !row.name) return;
    const prevImgs = row.images || [];
    updateRowById(id, { status: "processing" });
    try {
      const { data } = await api.post("/search/ai-fallback", {
        items: [{ name: row.name, category: row.category || "" }],
      });
      llmCostRef.current += COST_GENERATE_FIELDS;
      if (data.llm_quota_exhausted) {
        toast.error("Saldo do Emergent LLM Key esgotado. Recarregue em Profile → Universal Key.", { duration: 10000 });
      }
      const res = (data.results || [])[0] || {};
      const newImgs = res.ok && res.images ? res.images : [];
      if (!newImgs.length) {
        updateRowById(id, {
          status: prevImgs.length ? "ready" : "no_images",
          vision_note: res.error || "IA não achou imagens novas — mantidas as anteriores",
        });
        toast.warning(`"${row.name}": nenhuma imagem nova encontrada em outras fontes`);
        flushRowsImmediate();
        return;
      }
      const aiNote = res.ai_suggestion
        ? `IA buscou: "${res.ai_suggestion.query}" · ${res.ai_suggestion.category}`
        : "";
      let finalImgs = newImgs;
      let uncertainImgs = [];
      let filteredCount = null;
      let visionStatus = "none";
      let visionNote = aiNote;
      if (useVisionFilter) {
        updateRowById(id, { status: "filtering", images: newImgs });
        try {
          const vRes = await api.post("/vision/filter-images", {
            name: row.name,
            category: row.category || "",
            brand: row.brand || "",
            images: newImgs,
          });
          llmCostRef.current += COST_VISION_FILTER;
          const kept = vRes.data?.kept || [];
          const uncertain = vRes.data?.uncertain || [];
          finalImgs = [...kept, ...uncertain];
          uncertainImgs = uncertain;
          filteredCount = Math.max(0, newImgs.length - finalImgs.length);
          visionStatus = kept.length > 0
            ? (uncertain.length > 0 ? "mixed" : "confident")
            : (uncertain.length > 0 ? "uncertain" : "rejected");
          visionNote = [aiNote, vRes.data?.note || ""].filter(Boolean).join(" · ");
        } catch (_ve) {
          visionNote = [aiNote, "Vision indisponível"].filter(Boolean).join(" · ");
        }
      }
      if (!finalImgs.length) {
        // Vision rejected all new images — keep the previous ones (if any)
        updateRowById(id, {
          status: prevImgs.length ? "ready" : "no_images",
          images: prevImgs,
          vision_note: [aiNote, "Vision rejeitou as imagens novas"].filter(Boolean).join(" · "),
        });
        toast.warning(`"${row.name}": imagens novas rejeitadas pelo Vision — mantidas as anteriores`);
        flushRowsImmediate();
        return;
      }
      updateRowById(id, {
        status: "ready",
        images: finalImgs,
        uncertain_images: uncertainImgs,
        vision_status: visionStatus,
        vision_filtered: filteredCount,
        vision_note: visionNote,
        selected: !row.ml_blocked,
      });
      setLlmCost(llmCostRef.current);
      flushRowsImmediate();
      toast.success(
        `"${row.name}": ${finalImgs.length} imagem(ns) nova(s)${visionStatus === "confident" ? " · ✓ OK" : visionStatus === "mixed" || visionStatus === "uncertain" ? " · revise" : ""}`
      );
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      updateRowById(id, {
        status: prevImgs.length ? "ready" : "no_images",
        vision_note: msg,
      });
      flushRowsImmediate();
      toast.error("Falha na nova busca: " + msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useVisionFilter]);

  // -- Retry only rows that failed to find images (status === "no_images") --
  // Preserves all already-fetched data (images, LLM outputs). Only calls the
  // new /search/ai-fallback endpoint on those rows to save LLM cost and time.
  const retryFailedImages = async () => {
    const failedRows = rowsRef.current.filter(
      (r) => r.status === "no_images" && r.name && (!r.images || r.images.length === 0)
    );
    if (!failedRows.length) {
      toast.error("Nenhum item marcado como SEM IMAGEM");
      return;
    }
    setRetryingFailed(true);
    setRetryProgress({ done: 0, total: failedRows.length });
    let recovered = 0;
    let quotaHit = false;

    // Process in small batches to keep server load reasonable
    const CHUNK = 5;
    for (let i = 0; i < failedRows.length; i += CHUNK) {
      if (quotaHit) break;
      const chunk = failedRows.slice(i, i + CHUNK);
      // Mark chunk as processing
      chunk.forEach((r) => updateRowById(r.id, { status: "processing" }));
      try {
        const { data } = await api.post("/search/ai-fallback", {
          items: chunk.map((r) => ({ name: r.name, category: r.category || "" })),
        });
        if (data.llm_quota_exhausted) quotaHit = true;
        (data.results || []).forEach((res, idx) => {
          const row = chunk[idx];
          if (!row) return;
          if (res.ok && res.images && res.images.length > 0) {
            recovered++;
            updateRowById(row.id, {
              status: "ready",
              images: res.images,
              selected: true,
              vision_note: res.ai_suggestion
                ? `IA sugeriu: "${res.ai_suggestion.query}" · ${res.ai_suggestion.category}`
                : "",
            });
          } else {
            updateRowById(row.id, {
              status: "no_images",
              vision_note: res.error || res.indication || "Sem correspondência mesmo com IA",
            });
          }
        });
        llmCostRef.current += chunk.length * COST_GENERATE_FIELDS;
        setLlmCost(llmCostRef.current);
      } catch (e) {
        const status = e.response?.status;
        if (status === 402) {
          quotaHit = true;
        }
        chunk.forEach((r) =>
          updateRowById(r.id, {
            status: "no_images",
            vision_note: e.response?.data?.detail || e.message,
          })
        );
      }
      setRetryProgress({ done: Math.min(i + CHUNK, failedRows.length), total: failedRows.length });
    }

    flushRowsImmediate();
    setRetryingFailed(false);
    if (quotaHit) {
      toast.error("Saldo do Emergent LLM Key esgotado. Recarregue em Profile → Universal Key.", { duration: 10000 });
    } else if (recovered > 0) {
      toast.success(`${recovered}/${failedRows.length} recuperado(s) via IA. Selecionados para publicar.`);
    } else {
      toast.warning("A IA não encontrou imagens para os itens restantes. Considere baixar o CSV para tratar manualmente.");
    }
  };

  // Select only rows that failed image lookup (status === "no_images")
  const selectOnlyFailed = () => {
    const failed = rowsRef.current.filter((r) => r.status === "no_images");
    if (!failed.length) {
      toast.error("Nenhum item marcado como SEM IMAGEM");
      return;
    }
    rowsRef.current = rowsRef.current.map((r) => ({ ...r, selected: r.status === "no_images" }));
    setRows(rowsRef.current.slice());
    toast.success(`${failed.length} item(s) sem imagem selecionado(s)`);
  };

  // Select rows that failed to publish (status "publish_error" or "duplicate").
  // Lets the user retry only the problematic ones after a partial publish run.
  const selectNotPublished = () => {
    const bad = rowsRef.current.filter((r) => r.status === "publish_error" || r.status === "duplicate");
    if (!bad.length) {
      toast.error("Nenhum item pendente ou com erro de publicação");
      return;
    }
    rowsRef.current = rowsRef.current.map((r) => ({
      ...r,
      selected: r.status === "publish_error" || r.status === "duplicate",
    }));
    setRows(rowsRef.current.slice());
    toast.success(`${bad.length} item(s) não publicados selecionado(s)`);
  };

  // Re-run image search for SELECTED rows only. Clears their current images
  // first, then hits /search + /vision-filter (if enabled) exactly like
  // prepareAll — but no LLM /generate-fields to save cost.
  const refetchImagesSelected = async () => {
    const targets = rowsRef.current.filter((r) => r.selected && r.name);
    if (!targets.length) {
      toast.error("Selecione ao menos 1 item para buscar imagens novas");
      return;
    }
    if (preparing || publishing || retryingFailed) return;
    setRetryingFailed(true);
    setRetryProgress({ done: 0, total: targets.length });
    let recovered = 0;
    for (let i = 0; i < targets.length; i++) {
      const row = targets[i];
      updateRowById(row.id, { status: "processing", images: [] });
      try {
        const { data } = await api.post("/search", { items: [{ name: row.name, category: row.category || "", brand: row.brand || "" }] });
        const imgs = data?.[0]?.images || [];
        updateRowById(row.id, {
          status: imgs.length ? "ready" : "no_images",
          images: imgs,
          selected: imgs.length > 0,
        });
        if (imgs.length) recovered++;
      } catch (e) {
        updateRowById(row.id, { status: "no_images", vision_note: e.message });
      }
      setRetryProgress({ done: i + 1, total: targets.length });
    }
    flushRowsImmediate();
    setRetryingFailed(false);
    toast.success(`${recovered}/${targets.length} recuperado(s)`);
  };

  // Re-run /generate-fields for SELECTED rows only (LLM cost applies).
  // Overwrites description, seo, sku, dims, weight, indication — but PRESERVES
  // CSV-provided prices (item.price / item.promotional_price are never touched).
  const prepareLLMSelected = async () => {
    const targets = rowsRef.current.filter((r) => r.selected && r.name);
    if (!targets.length) {
      toast.error("Selecione ao menos 1 item para preparar com IA");
      return;
    }
    if (preparing || publishing || retryingFailed) return;
    if (!window.confirm(`Rodar IA em ${targets.length} item(s) selecionado(s)? Isso consome tokens de IA.`)) return;
    setRetryingFailed(true);
    setRetryProgress({ done: 0, total: targets.length });
    let done = 0;
    for (let i = 0; i < targets.length; i++) {
      const row = targets[i];
      try {
        const { data: f } = await api.post("/generate-fields", { name: row.name, category: row.category });
        llmCostRef.current += COST_GENERATE_FIELDS;
        updateRowById(row.id, {
          sku: row.sku || f.sku || "",
          handle: row.handle || f.handle || "",
          description: f.description || row.description || "",
          usage: f.usage || row.usage || "",
          seo_title: f.seo_title || row.seo_title || "",
          seo_description: f.seo_description || row.seo_description || "",
          stock: row.stock || (f.stock != null ? String(f.stock) : ""),
          weight: row.weight || (f.weight != null ? String(f.weight) : ""),
          height: row.height || (f.height != null ? String(f.height) : ""),
          width: row.width || (f.width != null ? String(f.width) : ""),
          depth: row.depth || (f.depth != null ? String(f.depth) : ""),
        });
        done++;
      } catch (e) {
        // silent — keep going for the batch
      }
      setRetryProgress({ done: i + 1, total: targets.length });
    }
    setLlmCost(llmCostRef.current);
    flushRowsImmediate();
    setRetryingFailed(false);
    toast.success(`IA rodada em ${done}/${targets.length}`);
  };

  // Force stock = 1 for all rows (safer default for retail — 1 unit shows the
  // product without over-committing inventory before the seller reconciles).
  const setStockAllOne = () => {
    if (!rowsRef.current.length) return;
    rowsRef.current = rowsRef.current.map((r) => ({ ...r, stock: "1" }));
    setRows(rowsRef.current.slice());
    toast.success("Estoque = 1 aplicado a todos");
  };

  // Force stock = 999999 (effectively unlimited on Nuvemshop). Used for
  // services / made-to-order / dropshipping where inventory is not tracked.
  const setStockAllInfinite = () => {
    if (!rowsRef.current.length) return;
    rowsRef.current = rowsRef.current.map((r) => ({ ...r, stock: "999999" }));
    setRows(rowsRef.current.slice());
    toast.success("Estoque ilimitado (999.999) aplicado a todos");
  };

  // Download CSV containing only the rows that failed to find images
  const downloadFailedCsv = () => {
    const failed = rowsRef.current.filter((r) => r.status === "no_images");
    if (!failed.length) {
      toast.error("Nenhum item sem imagem para exportar");
      return;
    }
    const esc = (v) => {
      const s = String(v == null ? "" : v);
      return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = [
      "nome", "categoria", "preco", "preco_promocional", "sku",
      "descricao", "seo_title", "seo_description", "indicacao",
      "estoque", "peso", "altura", "largura", "comprimento", "motivo_falha",
    ];
    const lines = [header.join(",")];
    for (const r of failed) {
      lines.push([
        esc(r.name),
        esc(r.category),
        esc(r.price),
        esc(r.promotional_price),
        esc(r.sku),
        esc((r.description || "").replace(/<[^>]+>/g, " ").trim()),
        esc(r.seo_title),
        esc(r.seo_description),
        esc(r.usage),
        esc(r.stock),
        esc(r.weight),
        esc(r.height),
        esc(r.width),
        esc(r.depth),
        esc(r.vision_note || "Sem imagens"),
      ].join(","));
    }
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `lote_sem_imagem_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`CSV com ${failed.length} item(s) sem imagem gerado`);
  };

  const downloadBatchZip = async () => {
    const eligible = rowsRef.current.filter((r) => (r.images && r.images.length > 0));
    if (!eligible.length) {
      toast.error("Nenhum item com imagens para exportar");
      return;
    }
    setDownloadingZip(true);
    try {
      const results = eligible.map((r) => ({
        input_name: r.name,
        input_category: r.category || "",
        name: r.name,
        description: (r.description || "").replace(/<[^>]+>/g, " ").slice(0, 500),
        images: r.images || [],
        source: "batch",
        indication: r.usage || "",
        status: r.status === "published" ? "published" : (r.status || "ready"),
      }));
      const res = await api.post("/export/zip", { results }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lote_${new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "")}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`ZIP com ${eligible.length} itens gerado`);
    } catch (e) {
      toast.error("Falha ao gerar ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  const stats = useMemo(() => {
    let ready = 0, noImg = 0, err = 0, pub = 0, selectedCount = 0, visFiltered = 0, notPublished = 0, pending = 0, selectedEligible = 0, selectable = 0;
    for (const r of rows) {
      if (r.status === "ready") ready++;
      else if (r.status === "no_images") noImg++;
      else if (r.status === "error") err++;
      else if (r.status === "published") pub++;
      // Publish failures + duplicates are counted separately so we can offer a
      // "Selecionar não publicados" quick-action after a partial publish run.
      if (r.status === "publish_error" || r.status === "duplicate") notPublished++;
      if (r.status === "queued" || r.status === "processing" || r.status === "filtering") pending++;
      if (r.status !== "error" && r.status !== "processing" && r.status !== "filtering" && !r.ml_blocked) selectable++;
      if (r.selected) {
        selectedCount++;
        // Only rows the publish loop will actually attempt (not yet published)
        if (r.status !== "published" && r.status !== "publishing") selectedEligible++;
      }
      if (typeof r.vision_filtered === "number") visFiltered += r.vision_filtered;
    }
    return { ready, noImg, err, pub, selectedCount, visFiltered, notPublished, pending, selectedEligible, selectable };
  }, [rows]);

  // Rows eligible for Nuvemshop recategorization (published + spreadsheet category)
  const recatCount = useMemo(
    () => rows.filter((r) => r.nuvemshop_id && (r.category || "").trim()).length,
    [rows]
  );

  // Total operational cost = (plan_price / plan_limit) * items_processed (BRL).
  // "Processed" = rows that are done (ready, no_images, error, published) — i.e.
  // items that already consumed the plan quota. Matches the (699/10000) formula
  // the user requested.
  const processedCount = stats.ready + stats.noImg + stats.err + stats.pub;
  const opCostBRL = opCostPerItemBRL * processedCount;
  const fmtBRL = (v) => `R$ ${v.toFixed(2).replace(".", ",")}`;

  // Client-side filter over the table by product name substring (case-insensitive,
  // accent-insensitive). Used for the search box above the table.
  const filteredRows = useMemo(() => {
    const q = rowFilter.trim().toLowerCase();
    if (!q) return rows;
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const qn = norm(q);
    return rows.filter((r) => norm(r.name).includes(qn) || norm(r.sku).includes(qn));
  }, [rows, rowFilter]);

  return (
    <div className="space-y-6 min-w-0">
      {/* Input */}
      <section className="border border-zinc-200 bg-white rounded-md p-5">
        <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
          <div className="eyebrow">Passo 1 · Adicione produtos em lote (até {batchMaxSize.toLocaleString("pt-BR")})</div>
          <div className="flex items-center gap-1 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">Teste de carga:</span>
            {[10, 100, 500, 1000, 5000, 10000].filter((n) => n <= testAllowedLimit).map((n) => (
              <Button
                key={n}
                variant="outline"
                onClick={() => loadSynthetic(n)}
                className="h-7 px-2 rounded-md border-zinc-300 text-[11px] font-mono"
                data-testid={`batch-synthetic-${n}`}
              >
                <Sparkles className="w-3 h-3 mr-1" />
                {n} itens
              </Button>
            ))}
            {testAllowedLimit < 1000 && (
              <span
                className="text-[10px] font-mono text-zinc-400 ml-1"
                data-testid="batch-synthetic-locked-hint"
                title="Assine um plano compatível para liberar testes maiores"
              >
                {isActivePaid ? `(máx ${testAllowedLimit} p/ seu plano)` : "(trial: só 10 · assine p/ mais)"}
              </span>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={`Cole 1 produto por linha: Nome, Categoria\n\nDipirona sódica 500mg, medicamento\nShampoo Pantene Hidratação 400ml, cosmético\nRação Golden 15kg, pet\n...`}
              rows={8}
              className="font-mono text-xs border-zinc-300"
              data-testid="batch-textarea"
            />
            <div className="text-xs text-zinc-500 mt-2 font-mono">
              {parsedItems.length} itens detectados. Máx {batchMaxSize.toLocaleString("pt-BR")}.
            </div>
          </div>
          <div className="border-2 border-dashed border-zinc-300 rounded-md p-6 text-center bg-zinc-50/50 flex flex-col justify-center">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden"
                   onChange={(e) => handleFile(e.target.files?.[0])} data-testid="batch-file-input" />
            {!fileName ? (
              <>
                <Upload className="w-6 h-6 text-zinc-400 mx-auto mb-2" />
                <p className="text-sm text-zinc-700 font-medium mb-1">Envie sua planilha (XLSX recomendado · CSV também aceito)</p>
                <p className="text-xs text-zinc-500 mb-1">
                  Colunas: <span className="font-mono font-semibold text-[#002FA7]">nome</span>, <span className="font-mono">categoria</span>, <span className="font-mono text-emerald-700 font-semibold">preco</span>, <span className="font-mono text-emerald-700 font-semibold">preco_promocional</span>, sku, estoque, peso, altura, largura, comprimento, descricao, seo_title
                </p>
                <p className="text-[10px] text-zinc-400 mb-3">
                  Baixe o <strong>Modelo XLSX</strong> abaixo — ele já vem com as colunas certas, dicas em cada cabeçalho e exemplos preenchidos.
                </p>
                <div className="flex gap-2 justify-center flex-wrap">
                  <Button variant="outline" onClick={() => fileRef.current?.click()} className="rounded-md border-zinc-300" data-testid="batch-upload-button">
                    Selecionar arquivo
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={downloadTemplate}
                    className="rounded-md text-[11px] font-mono text-[#002FA7] hover:text-[#001F80]"
                    data-testid="batch-template-download"
                  >
                    <Download className="w-3 h-3 mr-1" />
                    Modelo XLSX
                  </Button>
                </div>
              </>
            ) : (
              <div className="bg-white border border-zinc-200 rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileSpreadsheet className="w-4 h-4 text-[#002FA7]" />
                    <div className="text-left min-w-0">
                      <div className="text-sm font-medium truncate">{fileName}</div>
                      <div className="text-xs font-mono text-zinc-500">
                        {fileItems.length} itens
                        {headersDetected && <span className="ml-1 text-emerald-700">· cabeçalhos detectados</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={clearFile} className="text-zinc-400 hover:text-zinc-700" data-testid="batch-clear-file"><X className="w-4 h-4" /></button>
                </div>
                {parseDiag && headersDetected && (
                  <div className="text-[10px] font-mono border-t border-zinc-100 pt-2 space-y-1" data-testid="batch-parse-diagnostic">
                    <div className="flex flex-wrap gap-1">
                      <span className="text-zinc-500">Colunas usadas:</span>
                      {parseDiag.detected.map((c) => {
                        const isPrice = c === "price" || c === "promotional_price";
                        return (
                          <span
                            key={c}
                            className={`px-1.5 py-0.5 rounded ${isPrice ? "bg-emerald-100 text-emerald-800 font-semibold" : "bg-zinc-100 text-zinc-700"}`}
                          >
                            {c}
                          </span>
                        );
                      })}
                    </div>
                    {(!parseDiag.detected.includes("price") || !parseDiag.detected.includes("promotional_price")) && (
                      <div className="text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1">
                        ⚠ Faltando: {!parseDiag.detected.includes("price") && <b>preco </b>}
                        {!parseDiag.detected.includes("promotional_price") && <b>preco_promocional </b>}
                        — renomeie as colunas do seu CSV para EXATAMENTE esses nomes (sem acento, sem espaço).
                      </div>
                    )}
                    {parseDiag.unmatched.length > 0 && (
                      <div className="text-zinc-500">
                        Colunas ignoradas: <span className="text-zinc-700">{parseDiag.unmatched.join(", ")}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-5 pt-4 border-t border-zinc-200 gap-3 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-xs text-zinc-500 font-mono">
              {preparing ? `Preparando ${prepProgress.done}/${prepProgress.total}...` : `${parsedItems.length} produtos aguardando`}
            </div>
            <label className="inline-flex items-center gap-2 text-xs font-mono text-zinc-700 cursor-pointer select-none" title="Descarta imagens que não parecem ser do produto (rótulo, bula, ambiente etc.)">
              <input
                type="checkbox"
                checked={useVisionFilter}
                onChange={(e) => setUseVisionFilter(e.target.checked)}
                className="rounded"
                data-testid="batch-vision-toggle"
                disabled={preparing}
              />
              <ImageIcon className="w-3 h-3 text-violet-600" />
              Filtro Vision (Gemini)
            </label>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={importFromNuvemshop}
              disabled={importingNS || preparing || publishing}
              variant="outline"
              className="h-11 px-5 rounded-md border-indigo-400 text-indigo-800 hover:bg-indigo-50 font-medium"
              data-testid="batch-import-ns-button"
              title="Lista os produtos já existentes na sua Nuvemshop (preços, descrições e imagens vêm prontos) e a IA completa somente a indicação de uso e as medidas — depois é só selecionar Mercado Livre e publicar os anúncios"
            >
              {importingNS ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              {importingNS
                ? (nsImportProgress.total
                    ? `${nsImportProgress.phase} ${nsImportProgress.done}/${nsImportProgress.total}`
                    : nsImportProgress.phase || "Importando...")
                : "Importar da Nuvemshop → ML"}
            </Button>
            {priceUpdatableItems.length > 0 && (
              <Button
                onClick={updatePricesOnly}
                disabled={updatingPrices || preparing}
                variant="outline"
                className="h-11 px-5 rounded-md border-teal-500 text-teal-800 hover:bg-teal-50 font-medium"
                data-testid="batch-update-prices-button"
                title="Localiza cada produto no ERP pelo SKU ou código de barras da planilha e atualiza SOMENTE preço, promoção e estoque (quando a coluna vier na planilha) — sem criar produto novo"
              >
                {updatingPrices ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Timer className="w-4 h-4 mr-2 hidden" />}
                {updatingPrices
                  ? `Atualizando ${priceProgress.done}/${priceProgress.total}...`
                  : `$ Atualizar preços/estoque (${priceUpdatableItems.length})`}
              </Button>
            )}
            {preparing && (
              <Button
                onClick={() => {
                  pausePrepRef.current = true;
                  toast.info("Pausando... aguardando os itens em andamento terminarem", { icon: "⏸️" });
                }}
                variant="outline"
                className="h-11 px-5 rounded-md border-amber-400 text-amber-800 hover:bg-amber-50 font-medium"
                data-testid="batch-pause-button"
                title="Pausa a busca de imagens. Os itens já preparados ficam salvos — use 'Salvar lote' e retome depois com 'Continuar preparação'"
              >
                <Timer className="w-4 h-4 mr-2" />
                Pausar
              </Button>
            )}
            {!preparing && stats.pending > 0 && (
              <Button
                onClick={resumePreparation}
                variant="outline"
                className="h-11 px-5 rounded-md border-emerald-400 text-emerald-800 hover:bg-emerald-50 font-medium"
                data-testid="batch-resume-button"
                title="Retoma a busca de imagens e preparação IA apenas dos itens que ficaram na fila"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Continuar preparação ({stats.pending.toLocaleString("pt-BR")})
              </Button>
            )}
            <Button
              onClick={prepareAll}
              disabled={preparing || parsedItems.length === 0}
              className="h-11 px-6 rounded-md brand-primary text-white font-medium"
              data-testid="batch-prepare-button"
            >
              {preparing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
              {preparing ? "Preparando..." : "Preparar produtos com IA"}
            </Button>
          </div>
        </div>
        {preparing && (
          <div className="mt-3">
            <Progress value={prepProgress.total ? (prepProgress.done / prepProgress.total) * 100 : 0} className="h-1.5" />
            <div className="text-[10px] font-mono text-zinc-500 mt-1 text-right">
              {prepProgress.done}/{prepProgress.total} · {fmtDuration(prepTiming ? Date.now() - prepTiming.startMs : 0)}
            </div>
          </div>
        )}
      </section>

      {/* Results table */}
      {rows.length > 0 && (
        <section className="border border-zinc-200 bg-white rounded-md overflow-hidden">
          <div className="p-4 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="eyebrow mb-1">Passo 2 · Revise e publique</div>
              <h3 className="font-display text-lg font-semibold text-[#09090B]">
                {rows.length} itens · <span className="text-emerald-700">{stats.ready} prontos</span>
                {stats.noImg > 0 && <> · <span className="text-amber-700">{stats.noImg} sem imagem</span></>}
                {stats.err > 0 && <> · <span className="text-red-700">{stats.err} erros</span></>}
                {stats.pub > 0 && <> · <span className="text-emerald-800">{stats.pub} publicados</span></>}
                {stats.visFiltered > 0 && <> · <span className="text-violet-700">{stats.visFiltered} img filtradas</span></>}
                {llmCost > 0 && <> · <span className="text-violet-800">≈ ${llmCost.toFixed(3)} em IA</span></>}
                {opCostBRL > 0 && (
                  <> · <span className="text-[#002FA7]" title={`Custo operacional: ${fmtBRL(planPriceBRL)}/mês ÷ ${planLimitForOp.toLocaleString('pt-BR')} itens × ${processedCount} processados`}>{fmtBRL(opCostBRL)} operacional</span></>
                )}
              </h3>
              {(prepTiming || pubTiming) && (
                <div className="text-[11px] font-mono text-zinc-500 mt-1 flex items-center gap-3 flex-wrap" data-testid="batch-timing">
                  {prepTiming && (
                    <span className="inline-flex items-center gap-1">
                      <Timer className="w-3 h-3" />
                      Preparação: {fmtDuration((prepTiming.endMs || Date.now()) - prepTiming.startMs)}
                      {prepTiming.endMs && rows.length > 0 && (
                        <> · {((prepTiming.endMs - prepTiming.startMs) / rows.length / 1000).toFixed(2)}s/item</>
                      )}
                    </span>
                  )}
                  {pubTiming && (
                    <span className="inline-flex items-center gap-1">
                      <Timer className="w-3 h-3" />
                      Publicação: {fmtDuration((pubTiming.endMs || Date.now()) - pubTiming.startMs)}
                      {pubTiming.endMs && pubProgress.total > 0 && (
                        <> · {((pubTiming.endMs - pubTiming.startMs) / pubProgress.total / 1000).toFixed(2)}s/item</>
                      )}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              {connectedErps.length > 1 && (
                <div className="flex items-center gap-1.5" data-testid="batch-erp-selector">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Publicar em:</span>
                  <select
                    value={publishTarget}
                    onChange={(e) => setPublishTarget(e.target.value)}
                    className="h-9 rounded-md border border-zinc-300 bg-white text-xs px-2 font-medium text-zinc-900"
                    data-testid="batch-erp-target-select"
                  >
                    {connectedErps.map((e) => (
                      <option key={e.key} value={e.key}>{e.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {connectedErps.length === 1 && (
                <span
                  className="text-[10px] font-mono uppercase tracking-widest text-zinc-500"
                  data-testid="batch-erp-single-target"
                >
                  Publicando em <strong className="text-[#002FA7]">{connectedErps[0].name}</strong>
                </span>
              )}
              {connectedErps.length === 0 && (
                <span
                  className="text-[11px] font-mono text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1"
                  data-testid="batch-erp-none-warning"
                >
                  Nenhum ERP conectado · <a href="/settings" className="underline">Conectar em Configurações</a>
                </span>
              )}
              {publishTarget === "mercadolivre" && mlBlockedCount > 0 && (
                <span
                  className="text-[11px] font-mono text-red-800 bg-red-50 border border-red-300 rounded-md px-2 py-1"
                  data-testid="ml-blocked-summary"
                  title="Medicamentos humanos exigem ANVISA/ATC por SKU — não publicáveis em lote no ML"
                >
                  💊 {mlBlockedCount} medicamento(s) humano(s) pulado(s) no ML
                </span>
              )}
              <label
                className="flex items-center gap-1.5 text-[11px] font-mono text-zinc-600 cursor-pointer select-none"
                title="Se marcado, republica mesmo se o produto já foi publicado antes (mesmo SKU ou nome)"
              >
                <input
                  type="checkbox"
                  checked={forceDuplicates}
                  onChange={(e) => setForceDuplicates(e.target.checked)}
                  className="w-3.5 h-3.5 accent-[#002FA7]"
                  data-testid="batch-force-duplicates"
                />
                Permitir duplicatas
              </label>
              <Button
                onClick={publishSelected}
                disabled={publishing || preparing || !canPublish || !publishTarget}
                className="rounded-md brand-primary text-white h-9 disabled:opacity-50"
                data-testid="batch-publish-button"
                title={
                  !canPublish
                    ? "Assinatura necessária para publicar"
                    : !publishTarget
                    ? "Conecte um ERP em Configurações"
                    : ""
                }
              >
                {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {publishing
                  ? `Publicando ${pubProgress.done}/${pubProgress.total} no ${connectedErps.find((e) => e.key === publishTarget)?.name || "ERP"}`
                  : !canPublish
                    ? "Assinatura necessária"
                    : `Publicar selecionados no ${connectedErps.find((e) => e.key === publishTarget)?.name || "ERP"} (${stats.selectedEligible})`}
              </Button>
              {stats.pub > 0 && (
                <Button
                  variant="outline"
                  onClick={bulkDeletePublished}
                  disabled={publishing || preparing || bulkDeleting}
                  className="rounded-md border-red-300 text-red-700 hover:bg-red-50 h-9"
                  data-testid="batch-bulk-delete-button"
                  title={`Apaga permanentemente do ${connectedErps.find((e) => e.key === publishTarget)?.name || "ERP"} os produtos publicados neste lote`}
                >
                  {bulkDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Apagar publicados ({stats.pub})
                </Button>
              )}
              {recatCount > 0 && (publishTarget || "nuvemshop") === "nuvemshop" && (
                <Button
                  variant="outline"
                  onClick={recategorizePublished}
                  disabled={publishing || preparing || recategorizing}
                  className="rounded-md border-emerald-300 text-emerald-700 hover:bg-emerald-50 h-9"
                  data-testid="batch-recategorize-button"
                  title="Move os produtos já publicados na Nuvemshop para a categoria da coluna 'categoria' da planilha (cria a categoria na loja se não existir)"
                >
                  {recategorizing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderTree className="w-4 h-4 mr-2" />}
                  {recategorizing
                    ? `Movendo ${recatProgress.done}/${recatProgress.total}`
                    : `Mover p/ categoria da planilha (${recatCount})`}
                </Button>
              )}
              <Button variant="outline" onClick={() => toggleAll(true)} className="rounded-md border-zinc-300 h-9">Selecionar prontos</Button>
              <Button variant="outline" onClick={() => toggleAll(false)} className="rounded-md border-zinc-300 h-9">Limpar</Button>
              <Button
                onClick={saveBatch}
                disabled={savingBatch || preparing || !rows.length}
                variant="outline"
                className="rounded-md border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5 h-9"
                data-testid="batch-save-button"
                title={batchId ? `Atualizar lote salvo "${batchName}"` : "Salva este lote para você reabrir depois em Meus Lotes"}
              >
                {savingBatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {batchId ? "Atualizar lote" : "Salvar lote"}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  if (preparing || publishing) return;
                  const hasStuff = rowsRef.current.length > 0;
                  if (hasStuff && !window.confirm("Limpar TODO o lote em andamento? As linhas atuais serão descartadas — CSV e URLs deverão ser reenviados.")) return;
                  batchStore.reset();
                  rowsRef.current = [];
                  setRows([]);
                  setFileItems([]);
                  setFileName("");
                  setHeadersDetected(false);
                  setParseDiag(null);
                  setInputText("");
                  setBatchId(null);
                  setBatchName("");
                  llmCostRef.current = 0;
                  setLlmCost(0);
                  toast.success("Lote descartado — pronto para nova planilha");
                }}
                disabled={preparing || publishing}
                className="rounded-md border-zinc-300 text-zinc-700 hover:bg-zinc-50 h-9"
                data-testid="batch-reset-button"
                title="Descarta o lote atual e limpa o estado guardado no navegador (localStorage). Use após atualizações de sistema."
              >
                <X className="w-4 h-4 mr-2" />
                Novo lote
              </Button>
              <Button
                variant="outline"
                onClick={setStockAllOne}
                disabled={preparing || publishing || !rows.length}
                className="rounded-md border-zinc-300 h-9"
                data-testid="batch-stock-all-one-button"
                title="Aplica estoque = 1 para todos os itens do lote"
              >
                Estoque 1 p/ todos
              </Button>
              <Button
                variant="outline"
                onClick={setStockAllInfinite}
                disabled={preparing || publishing || !rows.length}
                className="rounded-md border-zinc-300 h-9"
                data-testid="batch-stock-all-infinite-button"
                title="Aplica estoque = 999.999 (efetivamente ilimitado na Nuvemshop) para todos os itens"
              >
                Estoque ∞ p/ todos
              </Button>
              {stats.selectedCount > 0 && (
                <>
                  <Button
                    variant="outline"
                    onClick={refetchImagesSelected}
                    disabled={preparing || publishing || retryingFailed}
                    className="rounded-md border-violet-300 text-violet-800 hover:bg-violet-50 h-9"
                    data-testid="batch-refetch-images-selected-button"
                    title="Refaz a busca de imagens só nos itens selecionados. As imagens atuais são descartadas."
                  >
                    <ImageIcon className="w-4 h-4 mr-2" />
                    Buscar imagens novas ({stats.selectedCount})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={prepareLLMSelected}
                    disabled={preparing || publishing || retryingFailed}
                    className="rounded-md border-violet-300 text-violet-800 hover:bg-violet-50 h-9"
                    data-testid="batch-prepare-llm-selected-button"
                    title="Roda a IA (generate-fields) só nos itens selecionados. Consome tokens de IA. Preserva preços do CSV."
                  >
                    <Wand2 className="w-4 h-4 mr-2" />
                    Preparar IA ({stats.selectedCount})
                  </Button>
                </>
              )}
              {stats.notPublished > 0 && (
                <Button
                  variant="outline"
                  onClick={selectNotPublished}
                  disabled={preparing || publishing}
                  className="rounded-md border-red-300 text-red-700 hover:bg-red-50 h-9"
                  data-testid="batch-select-not-published-button"
                  title="Marca apenas os itens que ainda não foram publicados (erro/duplicado) para você reenviar só esses"
                >
                  Selecionar não publicados ({stats.notPublished})
                </Button>
              )}
              {stats.noImg > 0 && (
                <>
                  <Button
                    variant="outline"
                    onClick={retryFailedImages}
                    disabled={retryingFailed || preparing || publishing}
                    className="rounded-md border-amber-300 text-amber-800 hover:bg-amber-50 h-9"
                    data-testid="batch-retry-failed-button"
                    title="Usa IA para inferir uma categoria melhor e refazer só a busca de imagens dos itens SEM IMAGEM. Preserva todo o resto."
                  >
                    {retryingFailed ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                    {retryingFailed
                      ? `Buscando ${retryProgress.done}/${retryProgress.total}...`
                      : `Buscar novamente (${stats.noImg})`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={selectOnlyFailed}
                    disabled={retryingFailed || preparing || publishing}
                    className="rounded-md border-amber-300 text-amber-800 hover:bg-amber-50 h-9"
                    data-testid="batch-select-failed-button"
                    title="Marca apenas as linhas SEM IMAGEM"
                  >
                    Selecionar sem imagem
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadFailedCsv}
                    disabled={retryingFailed || preparing || publishing}
                    className="rounded-md border-amber-300 text-amber-800 hover:bg-amber-50 h-9"
                    data-testid="batch-download-failed-csv-button"
                    title="Baixa um CSV apenas com os itens SEM IMAGEM (para tratar manualmente)"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Baixar CSV sem imagem
                  </Button>
                </>
              )}
              <Button
                variant="outline"
                onClick={downloadBatchZip}
                disabled={downloadingZip || preparing}
                className="rounded-md border-zinc-300 h-9"
                data-testid="batch-zip-download-button"
              >
                {downloadingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                {downloadingZip ? "Gerando ZIP..." : "Baixar ZIP"}
              </Button>
            </div>
          </div>
          {publishing && (
            <Progress value={pubProgress.total ? (pubProgress.done / pubProgress.total) * 100 : 0} className="h-1 rounded-none" />
          )}

          <div className="px-3 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center gap-3">
            <div className="flex-1 relative max-w-md">
              <input
                type="text"
                placeholder="Pesquisar produto pelo nome..."
                value={rowFilter}
                onChange={(e) => setRowFilter(e.target.value)}
                className="w-full h-9 pl-8 pr-8 rounded-md border border-zinc-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                data-testid="batch-row-filter"
              />
              <svg className="absolute left-2 top-2.5 w-4 h-4 text-zinc-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              {rowFilter && (
                <button
                  onClick={() => setRowFilter("")}
                  className="absolute right-2 top-2 text-zinc-400 hover:text-zinc-700"
                  data-testid="batch-row-filter-clear"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="text-xs font-mono text-zinc-500">
              {rowFilter ? `${filteredRows.length}/${rows.length} itens` : `${rows.length} itens`}
            </div>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full min-w-[1600px] text-sm">
              <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0 z-10">
                <tr>
                  <th className="p-2 w-8 text-center">
                    <input
                      type="checkbox"
                      checked={stats.selectable > 0 && stats.selectedCount >= stats.selectable}
                      onChange={(e) => toggleSelectAllRows(e.target.checked)}
                      className="rounded"
                      title="Marcar = selecionar todos os itens · Desmarcar = limpar seleção"
                      data-testid="batch-select-all-checkbox"
                    />
                  </th>
                  <th className="p-2 eyebrow text-zinc-500 text-left w-[180px]">Produto</th>
                  <th className="p-2 eyebrow text-zinc-500 text-left w-[300px]">Descrição gerada</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[110px]">Imgs</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[100px]">Preço R$</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[100px]">Promo</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[80px]">Estoque</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[80px]" title="Peso em kg">Peso kg</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[80px]" title="Altura em cm">Alt cm</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[80px]" title="Largura em cm">Larg cm</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[80px]" title="Comprimento em cm">Comp cm</th>
                  <th className="p-2 eyebrow text-zinc-500 w-[130px]">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => (
                  <BatchRow key={r.id} r={r} onToggle={toggleRow} onEditPrice={editPrice} onEditField={editField} onShowError={setErrorModalRow} onPasteManualImage={pasteManualImage} onPasteManualCategory={pasteManualCategory} onPasteManualAnvisa={pasteManualAnvisa} onAddRxTerm={addRxTerm} onRetryImages={retryRowImages} publishTarget={publishTarget} />
                ))}
                {filteredRows.length === 0 && rowFilter && (
                  <tr><td colSpan={12} className="p-8 text-center text-zinc-500 text-sm">Nenhum item corresponde a "{rowFilter}"</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Floating status card (sticky bottom-right while any op is running) */}
      {(preparing || publishing) && (
        <div
          className="fixed bottom-6 right-6 z-50 bg-white border border-zinc-200 rounded-md shadow-lg p-4 min-w-[280px] max-w-[360px]"
          data-testid="batch-floating-status"
        >
          <div className="eyebrow mb-2 flex items-center gap-2">
            <Loader2 className="w-3 h-3 animate-spin text-[#002FA7]" />
            {preparing ? "Preparando produtos" : "Publicando no Nuvemshop"}
          </div>
          {preparing && (
            <>
              <div className="text-xs font-mono text-zinc-700 mb-1">
                {prepProgress.done}/{prepProgress.total} · {fmtDuration(prepTiming ? Date.now() - prepTiming.startMs : 0)}
                {prepProgress.done > 0 && prepTiming && (
                  <> · <span className="text-zinc-500">{(((Date.now() - prepTiming.startMs) / prepProgress.done) / 1000).toFixed(1)}s/item</span></>
                )}
              </div>
              <Progress value={prepProgress.total ? (prepProgress.done / prepProgress.total) * 100 : 0} className="h-1.5" />
              <div className="text-[10px] font-mono text-zinc-500 mt-2 flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {stats.ready} prontos
                {stats.noImg > 0 && <><span className="text-amber-700">· {stats.noImg} sem imagem</span></>}
                {stats.err > 0 && <><span className="text-red-700">· {stats.err} erros</span></>}
              </div>
              {(llmCost > 0 || opCostBRL > 0) && (
                <div className="text-[10px] font-mono text-zinc-500 mt-1 space-y-0.5" data-testid="batch-cost-summary">
                  {llmCost > 0 && (
                    <div className="flex items-center gap-1" data-testid="batch-llm-cost">
                      <span className="text-violet-700">≈</span>
                      <span className="font-semibold text-zinc-700">${llmCost.toFixed(3)}</span>
                      <span className="text-zinc-400">em IA</span>
                    </div>
                  )}
                  {opCostBRL > 0 && (
                    <div className="flex items-center gap-1" data-testid="batch-op-cost">
                      <span className="text-[#002FA7]">≈</span>
                      <span className="font-semibold text-zinc-700">{fmtBRL(opCostBRL)}</span>
                      <span className="text-zinc-400">operacional ({fmtBRL(planPriceBRL)}/{planLimitForOp.toLocaleString('pt-BR')})</span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {publishing && (
            <>
              <div className="text-xs font-mono text-zinc-700 mb-1">
                <span className="text-[#002FA7]">Publicando no {connectedErps.find((e) => e.key === publishTarget)?.name || "ERP"}</span>
                <br />
                {pubProgress.done}/{pubProgress.total} · {fmtDuration(pubTiming ? Date.now() - pubTiming.startMs : 0)}
                {pubProgress.done > 0 && pubTiming && (
                  <> · <span className="text-zinc-500">{(((Date.now() - pubTiming.startMs) / pubProgress.done) / 1000).toFixed(1)}s/item</span></>
                )}
              </div>
              <Progress value={pubProgress.total ? (pubProgress.done / pubProgress.total) * 100 : 0} className="h-1.5" />
              <div className="text-[10px] font-mono text-zinc-500 mt-2 flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" /> {stats.pub} publicados
              </div>
            </>
          )}
          {/* tick keeps this card refreshing every 1s so elapsed time updates live */}
          <span className="hidden">{tick}</span>
        </div>
      )}

      {/* Error detail modal — opens when user clicks a FALHOU chip */}
      {errorModalRow && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setErrorModalRow(null)}
          data-testid="batch-error-modal"
        >
          <div
            className="bg-white rounded-md shadow-2xl max-w-lg w-full p-6 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="eyebrow mb-1 text-red-700">Falha na publicação</div>
                <div className="font-display font-semibold text-[#09090B] truncate max-w-sm" title={errorModalRow.name}>
                  {errorModalRow.name}
                </div>
              </div>
              <button
                onClick={() => setErrorModalRow(null)}
                className="text-zinc-400 hover:text-zinc-700"
                data-testid="batch-error-modal-close"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs font-mono text-red-900 whitespace-pre-wrap break-words max-h-64 overflow-y-auto">
              {errorModalRow.publish_message ||
                errorModalRow.error_msg ||
                errorModalRow.vision_note ||
                "Sem mensagem de erro detalhada."}
            </div>
            <div className="mt-4 text-xs text-zinc-600 space-y-1">
              <div><strong>Categoria:</strong> {errorModalRow.category || "—"}</div>
              <div><strong>Preço:</strong> {errorModalRow.price || "—"}</div>
              <div><strong>SKU:</strong> {errorModalRow.sku || "—"}</div>
              <div><strong>Imagens:</strong> {errorModalRow.images?.length || 0}</div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setErrorModalRow(null)}
                className="rounded-md border-zinc-300"
                data-testid="batch-error-modal-dismiss"
              >
                Fechar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* iter 51 — Post-publish report modal: failures grouped by reason */}
      {publishReport && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setPublishReport(null)}
          data-testid="publish-report-modal"
        >
          <div
            className="bg-white rounded-md shadow-2xl max-w-2xl w-full p-6 animate-in fade-in zoom-in-95 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="eyebrow mb-1 text-[#002FA7]">Relatório de publicação</div>
                <div className="font-display font-semibold text-[#09090B]">{publishReport.target}</div>
              </div>
              <button
                onClick={() => setPublishReport(null)}
                className="text-zinc-400 hover:text-zinc-700"
                data-testid="publish-report-close"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex gap-2 mb-4 text-xs font-mono">
              <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid="publish-report-success-count">
                ✓ {publishReport.success} publicado(s)
              </span>
              <span className="px-2 py-1 rounded bg-red-50 text-red-800 border border-red-200" data-testid="publish-report-fail-count">
                ✗ {publishReport.failures.length} falha(s)
              </span>
              {publishReport.duplicates.length > 0 && (
                <span className="px-2 py-1 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  ⧉ {publishReport.duplicates.length} duplicata(s)
                </span>
              )}
            </div>
            <div className="overflow-y-auto flex-1 space-y-3 pr-1">
              {Object.entries(
                publishReport.failures.reduce((acc, f) => {
                  const key = f.message || "Erro desconhecido";
                  (acc[key] = acc[key] || []).push(f.name);
                  return acc;
                }, {})
              ).map(([msg, names], i) => (
                <div key={i} className="border border-red-200 rounded-md p-3 bg-red-50/50">
                  <div className="text-xs font-mono text-red-900 whitespace-pre-wrap break-words mb-2">{msg}</div>
                  <div className="text-[11px] text-zinc-700">
                    <strong>{names.length} item(ns):</strong>{" "}
                    {names.slice(0, 6).join(" · ")}
                    {names.length > 6 && ` · e mais ${names.length - 6}...`}
                  </div>
                </div>
              ))}
              {publishReport.duplicates.length > 0 && (
                <div className="border border-amber-200 rounded-md p-3 bg-amber-50/50">
                  <div className="text-xs text-amber-900 mb-1">
                    Duplicatas (já publicados antes) — marque "Permitir duplicatas" para forçar reenvio:
                  </div>
                  <div className="text-[11px] text-zinc-700">
                    {publishReport.duplicates.slice(0, 6).map((d) => d.name).join(" · ")}
                    {publishReport.duplicates.length > 6 && ` · e mais ${publishReport.duplicates.length - 6}...`}
                  </div>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setPublishReport(null)}
                className="rounded-md border-zinc-300"
                data-testid="publish-report-dismiss"
              >
                Fechar
              </Button>
              {publishReport.failures.length > 0 && publishReport.kind !== "prices" && (
                <Button
                  onClick={resendReportFailures}
                  className="rounded-md brand-primary text-white"
                  data-testid="publish-report-resend"
                  title="Seleciona apenas os itens que falharam e publica novamente (edite os campos antes, se necessário)"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Reenviar falhas ({publishReport.failures.length})
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
